import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import * as d3 from 'd3';
import { COLOR_PALETTES } from './AdminBackend';
import { HelpGuide } from './HelpGuide';

const firebaseConfig = {
  apiKey: "AIzaSyCssPmZEr123f4QumVj8Uw-mZKPm4_L9pc",
  authDomain: "visualizer-f2095.firebaseapp.com",
  projectId: "visualizer-f2095",
  storageBucket: "visualizer-f2095.firebasestorage.app",
  messagingSenderId: "632056453495",
  appId: "1:632056453495:web:c22f700e14a66f5e06a972"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// VIEW TABS CONFIGURATION
const VIEW_TABS_CONFIG = {
  basic: {
    label: 'Basic',
    tabId: 'basic',
    description: 'Company List and Top Applicants analysis',
    defaultEnabled: true
  },
  innovation: {
    label: 'Innovation',
    tabId: 'innovation',
    description: 'Visualize LLM outputs',
    defaultEnabled: true
  },
  trends: {
    label: 'Trends',
    tabId: 'trends',
    description: 'Temporal analysis and trends',
    defaultEnabled: false
  },
  citations: {
    label: 'Advanced',
    tabId: 'citations',
    description: 'Advanced patent analysis: citations, valuation, and quality metrics',
    defaultEnabled: false
  },
  advanced: {
    label: 'My Collection',
    tabId: 'advanced',
    description: 'Tagged patents, custom categories, and notes',
    defaultEnabled: false
  }
};

// Helper function to get color palette from study
const getStudyColors = (selectedStudy, type = 'monochrome') => {
  const paletteKey = selectedStudy?.colorPalette || 'defaultBlue';
  const palette = COLOR_PALETTES[paletteKey] || COLOR_PALETTES.defaultBlue;
  return palette[type] || palette.monochrome;
};

// VALUATION METHODOLOGIES
const VALUATION_METHODS = {
  standard: {
    name: 'Standard',
    description: 'Equal weighting across all factors',
    weights: { citations: 0.333, family: 0.333, lifetime: 0.334 }
  },
  citationFocused: {
    name: 'Citation Focused',
    description: 'Emphasizes innovation impact and market influence',
    weights: { citations: 0.60, family: 0.20, lifetime: 0.20 }
  },
  geoCoverage: {
    name: 'Family Size',
    description: 'Prioritizes patent family size',
    weights: { citations: 0.20, family: 0.60, lifetime: 0.20 }
  },
  lifetimeValue: {
    name: 'Lifetime Value',
    description: 'Emphasizes remaining patent life',
    weights: { citations: 0.20, family: 0.20, lifetime: 0.60 }
  }
};

// CSV PARSING UTILITIES
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
};

const parseMultilineCSV = (text) => {
  const rows = [];
  const lines = text.split('\n');
  let currentRow = '';
  let inQuotes = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Count quotes in line
    for (let j = 0; j < line.length; j++) {
      if (line[j] === '"') {
        inQuotes = !inQuotes;
      }
    }

    currentRow += line;

    // If we're not inside quotes, this row is complete
    if (!inQuotes) {
      if (currentRow.trim()) {
        rows.push(currentRow);
      }
      currentRow = '';
    } else {
      // Still inside quotes, add newline and continue
      currentRow += '\n';
    }
  }

  return rows;
};

// ============================================
// BOOLEAN SEARCH UTILITIES
// ============================================
const parseSearchQuery = (query) => {
  if (!query || !query.trim()) return null;
  
  const tokens = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  
  while (i < query.length) {
    const char = query[i];
    
    if (char === '"') {
      if (inQuotes && current) {
        tokens.push({ type: 'phrase', value: current });
        current = '';
      }
      inQuotes = !inQuotes;
    } else if (!inQuotes && char === '(') {
      // Add current term if exists
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
      tokens.push({ type: 'bracket', value: '(' });
    } else if (!inQuotes && char === ')') {
      // Add current term if exists
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
      tokens.push({ type: 'bracket', value: ')' });
    } else if (!inQuotes && (char === '|' || char === '+' || char === '-')) {
      // Add current term if exists
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
      // Add operator
      if (char === '|') tokens.push({ type: 'operator', value: 'OR' });
      else if (char === '+') tokens.push({ type: 'operator', value: 'AND' });
      else if (char === '-') tokens.push({ type: 'operator', value: 'NOT' });
    } else if (!inQuotes && char === ' ') {
      if (current.trim()) {
        tokens.push({ type: 'term', value: current.trim() });
        current = '';
      }
    } else {
      current += char;
    }
    i++;
  }
  
  // If still in quotes, query is incomplete - return null to skip filtering
  if (inQuotes) return null;
  
  // Add final term
  if (current.trim()) {
    tokens.push({ type: 'term', value: current.trim() });
  }
  
  // Check for balanced brackets
  let bracketCount = 0;
  for (const token of tokens) {
    if (token.type === 'bracket') {
      if (token.value === '(') bracketCount++;
      else if (token.value === ')') bracketCount--;
      if (bracketCount < 0) return null; // Unbalanced
    }
  }
  if (bracketCount !== 0) return null; // Unbalanced
  
  // If no valid tokens, return null
  if (tokens.length === 0) return null;
  
  // If single term or phrase, return as-is
  if (tokens.length === 1) {
    return tokens[0];
  }
  
  // Default: space-separated terms = implicit AND
  return { type: 'parsed', tokens };
};

const evaluateSearch = (text, parsedQuery) => {
  if (!parsedQuery || !text) return false;
  
  const lowerText = text.toLowerCase();
  
  // Helper: convert wildcard to regex pattern
  const wildcardToRegex = (pattern) => {
    const escapedPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    const regexPattern = escapedPattern.replace(/\*/g, '.*');
    return new RegExp(regexPattern, 'i');
  };
  
  // Helper: check if text matches pattern (with wildcard support)
  const matchesPattern = (text, pattern) => {
    if (pattern.includes('*')) {
      // Wildcard: use pattern matching
      return wildcardToRegex(pattern).test(text);
    } else {
      // No wildcard: use word boundary matching
      const escapedPattern = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
      const wordBoundaryRegex = new RegExp('\\b' + escapedPattern + '\\b', 'i');
      return wordBoundaryRegex.test(text);
    }
  };
  
  // Single term or phrase
  if (parsedQuery.type === 'term' || parsedQuery.type === 'phrase') {
    return matchesPattern(lowerText, parsedQuery.value);
  }
  
  // Helper: process bracketed expressions recursively
  const processBrackets = (tokens) => {
    // Find innermost brackets and replace with evaluated result
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].type === 'bracket' && tokens[i].value === '(') {
        // Find matching closing bracket
        let depth = 1;
        let j = i + 1;
        while (j < tokens.length && depth > 0) {
          if (tokens[j].type === 'bracket') {
            if (tokens[j].value === '(') depth++;
            else if (tokens[j].value === ')') depth--;
          }
          j++;
        }
        
        // Extract bracketed tokens (excluding brackets themselves)
        const bracketedTokens = tokens.slice(i + 1, j - 1);
        
        // Recursively evaluate bracketed expression
        const bracketResult = evaluateTokens(bracketedTokens);
        
        // Replace bracketed section with result
        const resultToken = { type: 'result', value: bracketResult };
        tokens.splice(i, j - i, resultToken);
        
        // Process again from start (in case of nested brackets)
        return processBrackets(tokens);
      }
    }
    return tokens;
  };
  
  // Helper: evaluate tokens without brackets
  const evaluateTokens = (tokens) => {
    if (tokens.length === 0) return false;
    
    // If single result token, return its value
    if (tokens.length === 1 && tokens[0].type === 'result') {
      return tokens[0].value;
    }
    
    // If single term/phrase, evaluate it
    if (tokens.length === 1 && (tokens[0].type === 'term' || tokens[0].type === 'phrase')) {
      return matchesPattern(lowerText, tokens[0].value);
    }
    
    let results = [];
    let currentOp = 'AND'; // Default implicit AND
    let nextNot = false;
    
    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i];
      
      if (token.type === 'operator') {
        if (token.value === 'NOT') {
          nextNot = true;
        } else {
          currentOp = token.value;
        }
      } else if (token.type === 'result') {
        // Pre-evaluated result from bracket
        let found = token.value;
        
        if (nextNot) {
          found = !found;
          nextNot = false;
        }
        
        // Combine with previous results
        if (results.length === 0) {
          results.push(found);
        } else {
          if (currentOp === 'AND') {
            results.push(results.pop() && found);
          } else if (currentOp === 'OR') {
            results.push(results.pop() || found);
          }
          currentOp = 'AND'; // Reset to implicit AND
        }
      } else {
        // Evaluate term/phrase with wildcard support
        let found = matchesPattern(lowerText, token.value);
        
        if (nextNot) {
          found = !found;
          nextNot = false;
        }
        
        // Combine with previous results
        if (results.length === 0) {
          results.push(found);
        } else {
          if (currentOp === 'AND') {
            results.push(results.pop() && found);
          } else if (currentOp === 'OR') {
            results.push(results.pop() || found);
          }
          currentOp = 'AND'; // Reset to implicit AND
        }
      }
    }
    
    return results.length > 0 ? results[0] : false;
  };
  
  // Process brackets first (innermost to outermost)
  const tokens = parsedQuery.tokens;
  const processedTokens = processBrackets([...tokens]);
  
  // Evaluate final expression
  return evaluateTokens(processedTokens);
};

// ============================================
// CENTRALIZED FILTER FUNCTIONS
// ============================================

// Check if patent matches applicant filter
const matchesApplicant = (patent, selectedApplicant) => {
  if (!selectedApplicant) return true;
  
  if (patent.applicant.includes(';')) {
    return patent.applicant.split(';').map(a => a.trim()).includes(selectedApplicant);
  }
  return patent.applicant === selectedApplicant;
};

// Convert title to sentence case
const toSentenceCase = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// Normalize patent number (remove spaces, hyphens, slashes)
const normalizePatentNo = (str) => {
  if (!str) return '';
  return str.toUpperCase().replace(/[\s\-\/]/g, '');
};

// Check if patent matches text search filter
const matchesTextSearch = (patent, textSearch, searchableFields) => {
  if (!textSearch) return true;
  
  const parsedQuery = parseSearchQuery(textSearch);
  if (!parsedQuery) return true;
  
  const fields = Object.entries(searchableFields || {})
    .filter(([_, enabled]) => enabled)
    .map(([field, _]) => field);
  
  return fields.some(field => {
    const value = patent[field.toLowerCase()] || '';
    return evaluateSearch(value, parsedQuery);
  });
};

// ============================================
// PATENT VALUATION CALCULATIONS
// ============================================

const calculatePatentStatus = (patent) => {
  const filingYear = parseInt(patent.filingDate?.substring(0, 4));
  if (!filingYear) return null;
  
  const currentYear = new Date().getFullYear();
  const age = currentYear - filingYear;
  const expiryYear = filingYear + 20;
  
  const patentNo = (patent.patentNo || '').replace(/\s+/g, '').replace(/[\-\/]/g, '').toUpperCase();
  const jurisdiction = patentNo.substring(0, 2);
  
  let isGranted = false;
  let isPending = false;
  
  if (jurisdiction === 'WO') {
    isPending = true;
  } else {
    if (patentNo.match(/B\d*$/)) {
      isGranted = true;
    } else if (patentNo.match(/A\d+$/)) {
      isPending = true;
    } else if (jurisdiction === 'US' && patentNo.endsWith('A')) {
      isGranted = true;
    }
  }
  
  return {
    isGranted,
    isPending,
    age,
    expiryYear,
    isExpired: currentYear > expiryYear,
    isActive: currentYear <= expiryYear && isGranted,
    yearsRemaining: Math.max(0, expiryYear - currentYear)
  };
};

const calculateValuationScore = (patent, methodKey = 'standard', portfolioStats = null) => {
  const forwardCitations = parseInt(patent.citedBy) || 0;
  const familySize = parseInt(patent.familyCount) || 1;
  const status = calculatePatentStatus(patent);
  
  if (!status) return null;
  
  const patentNo = (patent.patentNo || '').toUpperCase();
  
  // Determine if granted (not A1/A2 application)
  const isGranted = !patentNo.includes('A1') && !patentNo.includes('A2');
  const statusMultiplier = isGranted ? 1.0 : 0.5;
  
  // Calculate citation velocity (citations per year)
  const citationVelocity = status.age > 0 ? forwardCitations / status.age : 0;
  
  // Min-Max scaling for each metric (0-100)
  const citationScore = portfolioStats ? 
    ((citationVelocity - portfolioStats.citationVelocity.min) / (portfolioStats.citationVelocity.max - portfolioStats.citationVelocity.min || 1)) * 100 : 
    Math.min(forwardCitations * 8, 100);
  
  const familyScore = portfolioStats ?
    ((familySize - portfolioStats.family.min) / (portfolioStats.family.max - portfolioStats.family.min || 1)) * 100 :
    Math.min(familySize * 15, 100);
  
  const yearsRemaining = status.isExpired ? 0 : status.yearsRemaining;
  const ageScore = portfolioStats ?
    ((yearsRemaining - portfolioStats.yearsRemaining.min) / (portfolioStats.yearsRemaining.max - portfolioStats.yearsRemaining.min || 1)) * 100 :
    (yearsRemaining / 20) * 100;
  
  const method = VALUATION_METHODS[methodKey] || VALUATION_METHODS.standard;
  const baseScore = (
    citationScore * method.weights.citations +
    familyScore * method.weights.family +
    ageScore * method.weights.lifetime
  ) / (method.weights.citations + method.weights.family + method.weights.lifetime);
  
  const totalScore = baseScore * statusMultiplier;
  
  return {
    score: Math.round(totalScore),
    valueTier: 'Low', // Placeholder, will be reassigned based on percentiles
    components: {
      citations: forwardCitations,
      citationVelocity: citationVelocity.toFixed(2),
      citationScore: Math.round(citationScore),
      familySize,
      familyScore: Math.round(familyScore),
      age: status.age,
      yearsRemaining,
      ageScore: Math.round(ageScore),
      isGranted,
      statusMultiplier
    },
    status
  };
};

const calculatePortfolioValuation = (patents, methodKey = 'standard') => {
  // Calculate portfolio statistics for min-max scaling (portfolio-wide)
  const validPatents = patents.filter(p => calculatePatentStatus(p) !== null);
  
  const citationVelocities = validPatents.map(p => {
    const citations = parseInt(p.citedBy) || 0;
    const status = calculatePatentStatus(p);
    return status && status.age > 0 ? citations / status.age : 0;
  });
  
  const portfolioStats = {
    citationVelocity: {
      min: Math.min(...citationVelocities),
      max: Math.max(...citationVelocities)
    },
    family: {
      min: Math.min(...validPatents.map(p => parseInt(p.familyCount) || 1)),
      max: Math.max(...validPatents.map(p => parseInt(p.familyCount) || 1))
    },
    yearsRemaining: {
      min: 0,
      max: Math.max(...validPatents.map(p => {
        const status = calculatePatentStatus(p);
        return status && !status.isExpired ? status.yearsRemaining : 0;
      }))
    }
  };
  
  const valuations = patents
    .map(p => ({ patent: p, valuation: calculateValuationScore(p, methodKey, portfolioStats) }))
    .filter(v => v.valuation !== null);
  
  // Sort by score to calculate percentile-based tiers
  const sortedScores = valuations.map(v => v.valuation.score).sort((a, b) => a - b);
  const p25 = d3.quantile(sortedScores, 0.25) || 0;
  const p50 = d3.quantile(sortedScores, 0.50) || 0;
  const p75 = d3.quantile(sortedScores, 0.75) || 0;
  
  // Reassign tiers based on percentiles
  valuations.forEach(v => {
    const score = v.valuation.score;
    if (score >= p75) v.valuation.valueTier = 'Very High';
    else if (score >= p50) v.valuation.valueTier = 'High';
    else if (score >= p25) v.valuation.valueTier = 'Medium';
    else v.valuation.valueTier = 'Low';
  });
  
  const l4Valuations = {};
  valuations.forEach(({ patent, valuation }) => {
    const l4 = patent.l4 || 'Uncategorized';
    if (!l4Valuations[l4]) {
      l4Valuations[l4] = {
        patents: [],
        totalScore: 0,
        avgScore: 0,
        count: 0
      };
    }
    l4Valuations[l4].patents.push({ patent, valuation });
    l4Valuations[l4].totalScore += valuation.score;
    l4Valuations[l4].count++;
  });
  
  Object.keys(l4Valuations).forEach(l4 => {
    l4Valuations[l4].avgScore = 
      Math.round(l4Valuations[l4].totalScore / l4Valuations[l4].count);
  });
  
  const totalScore = valuations.reduce((sum, v) => sum + v.valuation.score, 0);
  const avgScore = Math.round(totalScore / valuations.length);
  
  const tierCounts = {
    'Very High': 0,
    'High': 0,
    'Medium': 0,
    'Low': 0
  };
  valuations.forEach(v => {
    tierCounts[v.valuation.valueTier]++;
  });
  
  return {
    valuations,
    l4Valuations,
    portfolioMetrics: {
      totalPatents: valuations.length,
      avgScore,
      tierCounts,
      percentiles: { p25, p50, p75 }
    }
  };
};

// Check if patent matches L2 genus filter
const matchesGenus = (patent, selectedL2) => {
  if (!selectedL2) return true;
  return patent.l2 === selectedL2;
};

// Check if patent matches patent number search
const matchesPatentNo = (patent, patentNoSearch) => {
  if (!patentNoSearch) return true;
  const searchTerms = patentNoSearch.split(',').map(t => t.trim().toUpperCase()).filter(t => t);
  if (searchTerms.length === 0) return true;
  const patentNo = (patent.patentNo || '').replace(/[\s\-\/]/g, '').toUpperCase();
  // Support partial matching for dynamic filtering
  const result = searchTerms.some(term => {
    const cleanTerm = term.replace(/[\s\-\/]/g, '');
    return patentNo.includes(cleanTerm);
  });
  return result;
};

// Master filter function - applies all filters
const applyFilters = (patents, filters) => {
  const { selectedApplicant, textSearch, searchableFields, selectedL2, patentNoSearch } = filters;
  
  return patents.filter(patent => {
    return matchesApplicant(patent, selectedApplicant) &&
           matchesTextSearch(patent, textSearch, searchableFields) &&
           matchesGenus(patent, selectedL2) &&
           matchesPatentNo(patent, patentNoSearch);
  });
};

// ============================================
// CUSTOMER VIEW
// ============================================
function CustomerView({ user, onLogout }) {
  const [studies, setStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [patentData, setPatentData] = useState([]);
  const [applicants, setApplicants] = useState([]);
  
  // Control bar state
  const [view, setView] = useState('applicant-list');
  const [countMode, setCountMode] = useState('original');
  const [applicantSearch, setApplicantSearch] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [nSlider, setNSlider] = useState(10);
  const [velocityYears, setVelocityYears] = useState(3);
  const [selectedAlphabet, setSelectedAlphabet] = useState('ALL');
  const [expandedCollabs, setExpandedCollabs] = useState({});
  const [selectedExpiryL4, setSelectedExpiryL4] = useState('ALL');
  const [trendsJurisdiction, setTrendsJurisdiction] = useState('ALL');
  const [patentNoSearch, setPatentNoSearch] = useState('');
  
  // Load Lucide icons
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/lucide@latest';
    script.async = true;
    script.onload = () => {
      if (window.lucide) window.lucide.createIcons();
    };
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);
  
  // Reinitialize icons when content updates
  useEffect(() => {
    const timer = setTimeout(() => {
      if (window.lucide) window.lucide.createIcons();
    }, 100);
    return () => clearTimeout(timer);
  }, [activeTab, view, selectedStudy]);
  const [collaborationData, setCollaborationData] = useState({});
  const [selectedL4, setSelectedL4] = useState(null);
  const [deepDivesL4, setDeepDivesL4] = useState('');
  const [deepDivesCombos, setDeepDivesCombos] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [expandedL4Results, setExpandedL4Results] = useState(new Set());
  const [selectedL2, setSelectedL2] = useState('');
  // Valuation state
  const [valuationData, setValuationData] = useState(null);
  const [valuationMinScore, setValuationMinScore] = useState(50);
  const [valuationSortBy, setValuationSortBy] = useState('score');
  const [valuationMethod, setValuationMethod] = useState('standard');
  
  // Tag management
  const [taggedPatents, setTaggedPatents] = useState({});
  const [categories, setCategories] = useState(['Default']);
  const [whitespaceSelection, setWhitespaceSelection] = useState(null); // {l4, l7}
  
  // Load tags from localStorage on mount
  useEffect(() => {
    if (selectedStudy?.id) {
      const key = `tags_${selectedStudy.id}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        const { tags, cats } = JSON.parse(saved);
        setTaggedPatents(tags || {});
        setCategories(cats || ['Default']);
      }
    }
  }, [selectedStudy?.id]);
  
  // Save tags to localStorage whenever they change
  useEffect(() => {
    if (selectedStudy?.id && Object.keys(taggedPatents).length > 0) {
      const key = `tags_${selectedStudy.id}`;
      localStorage.setItem(key, JSON.stringify({ 
        tags: taggedPatents, 
        cats: categories 
      }));
    }
  }, [taggedPatents, categories, selectedStudy?.id]);
  
  // Toggle tag for a patent
  const toggleTag = (patentNo, category = 'Default') => {
    setTaggedPatents(prev => {
      const newTags = { ...prev };
      if (newTags[patentNo]) {
        delete newTags[patentNo];
      } else {
        newTags[patentNo] = {
          category,
          timestamp: new Date().toISOString(),
          note: ''
        };
      }
      return newTags;
    });
  };
  
  // Trends tab state
  const [trendsL4, setTrendsL4] = useState('');
  const [trendsL7, setTrendsL7] = useState('');
  const [trendsDateField, setTrendsDateField] = useState('filingDate');
  const [filingTrendsGroupBy, setFilingTrendsGroupBy] = useState('overall');
  
  const prevTabRef = useRef();

  // Auto-switch view when tab changes (only on actual tab switch)
  useEffect(() => {
    if (prevTabRef.current !== activeTab) {
      if (activeTab === 'basic') {
        setView('applicant-list');
      } else if (activeTab === 'innovation') {
        setView('rd-explorer');
        setSelectedL4(null); // Reset drill-down
      } else if (activeTab === 'trends') {
        setView('filing-trends');
        setFilingTrendsGroupBy('overall');
      } else if (activeTab === 'citations') {
        setView('impact-score');
      }
      // Clear text search when switching tabs
      setTextSearch('');
      setPatentNoSearch('');
      setSelectedApplicant('');
      setApplicantSearch('');
      prevTabRef.current = activeTab;
    }
  }, [activeTab]);

  // Simple view-based control visibility
  const shouldShowNSlider = () => {
    if (activeTab === 'basic') {
      return view === 'applicant-list' || view === 'technology-segments';
    }
    return false;
  };

  const shouldShowApplicantSearch = () => {
    if (activeTab === 'basic' && (view === 'applicant-list' || view === 'collaborations' || view === 'technology-segments')) {
      return false; // Hide for applicant list, collaborations, and R&D
    }
    if (activeTab === 'citations') {
      return false; // Hide for all Advanced tab views
    }
    return true;
  };

  const shouldShowTextSearch = () => {
    if (activeTab === 'basic' && (view === 'applicant-list' || view === 'collaborations' || view === 'technology-segments')) {
      return false; // Hide for applicant list, collaborations, and R&D
    }
    // Hide text search in Trends and Citations tabs
    if (activeTab === 'trends' || activeTab === 'citations') {
      return false;
    }
    return true;
  };
  // const [controlVisibility, setControlVisibility] = useState({}); // Unused - keeping for future use

  // Define which controls are visible by default for each view
  // const VIEW_CONTROLS_CONFIG = { // Unused - keeping for future use
  //   'company-list': {
  //     countToggle: true,
  //     applicantSearch: true,
  //     textSearch: true,
  //     nSlider: false,
  //     downloads: true
  //   },
  //   'top-applicants': {
  //     countToggle: true,
  //     applicantSearch: true,
  //     textSearch: true,
  //     nSlider: true,
  //     downloads: true
  //   }
  // };

  // Get visibility for a control (default config + overrides)
  // const isControlVisible = (controlName) => { // Unused - keeping for future use
  //   const defaultConfig = VIEW_CONTROLS_CONFIG[view] || {};
  //   if (controlVisibility[controlName] !== undefined) {
  //     return controlVisibility[controlName];
  //   }
  //   return defaultConfig[controlName] !== undefined ? defaultConfig[controlName] : true;
  // };

  const loadAvailableStudies = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'studies'));
      const allStudies = [];
      querySnapshot.forEach((doc) => {
        allStudies.push({ id: doc.id, ...doc.data() });
      });

      const userStudies = allStudies.filter(study => {
        if (study.archived) return false;
        if (!study.allowedUsers || study.allowedUsers.length === 0) return true;
        return study.allowedUsers.includes(user.email);
      });

      setStudies(userStudies);
      if (userStudies.length > 0) {
        loadStudyData(userStudies[0]);
      }
    } catch (err) {
      console.error('Error loading studies:', err);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadAvailableStudies();
  }, [user]);

  useEffect(() => {
    if (studies.length > 0 && !selectedStudy) {
      loadStudyData(studies[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studies]);

  const loadStudyData = async (study) => {
    setSelectedStudy(study);
    
    try {
      const mapping = study.mapping || {};
      let csvData = [];

      if (study.storageType === 'storage') {
        const response = await fetch(study.fileURL);
        const text = await response.text();
        const rows = parseMultilineCSV(text);
        
        const headers = parseCSVLine(rows[0]).map(h => h.replace(/^["']|["']$/g, '').trim());
        
        csvData = rows.slice(1)
          .filter(row => row.trim())
          .map(row => {
            const values = parseCSVLine(row).map(v => v.replace(/^["']|["']$/g, '').trim());
            const rowData = {};
            headers.forEach((header, index) => {
              rowData[header] = values[index] || '';
            });
            return rowData;
          });
      } else {
        csvData = study.csvData || [];
      }
      
      const data = csvData.map(row => {
        const getColValue = (fieldName) => {
          const colName = mapping[fieldName];
          return colName ? row[colName] : '';
        };

        return {
          patentNo: getColValue('Patent No.') || '',
          title: getColValue('Title') || '',
          abstract: getColValue('Abstract') || '',
          claims: getColValue('Claims') || '',
          applicant: getColValue('Applicant') || '',
          filingDate: getColValue('Filing date') || '',
          priorityDate: getColValue('Priority date') || '',
          publicationDate: getColValue('Publication date') || '',
          l1: getColValue('L1') || '',
          l2: getColValue('L2') || '',
          l3: getColValue('L3') || '',
          l4: getColValue('L4') || '',
          l5: getColValue('L5') || '',
          l6: getColValue('L6') || '',
          l7: getColValue('L7') || '',
          l8: getColValue('L8') || '',
          l9: getColValue('L9') || '',
          l10: getColValue('L10') || '',
          l11: getColValue('L11') || '',
          l12: getColValue('L12') || '',
          citationsMade: parseInt(getColValue('Citations Made')) || 0,
          citedBy: parseInt(getColValue('Cited by')) || 0,
          familyCount: parseInt(getColValue('Family member count')) || 0
        };
      });
      
      // Global deduplication by patent number
      const uniqueData = data.reduce((acc, patent) => {
        if (!acc.find(p => p.patentNo === patent.patentNo)) {
          acc.push(patent);
        }
        return acc;
      }, []);
      
      
      setPatentData(uniqueData);
      
      const allApplicants = new Set();
      data.forEach(d => {
        if (d.applicant.includes(';')) {
          d.applicant.split(';').forEach(a => allApplicants.add(a.trim()));
        } else {
          allApplicants.add(d.applicant);
        }
      });
      setApplicants([...allApplicants].filter(Boolean));
    } catch (err) {
      console.error('Error loading study data:', err);
    }
  };

  // Render Applicant List Treemap
  useEffect(() => {
    if (activeTab === 'basic' && view === 'applicant-list' && patentData.length > 0) {
      const container = document.getElementById('applicant-treemap');
      if (!container) return;

      d3.select(container).selectAll('*').remove();

      // Get applicants starting with selected alphabet or all applicants
      let applicantCounts;
      if (countMode === 'original') {
        const filteredData = selectedAlphabet === 'ALL' 
          ? patentData 
          : patentData.filter(d => d.applicant.charAt(0).toUpperCase() === selectedAlphabet);
        applicantCounts = d3.rollup(
          filteredData,
          v => v.length,
          d => d.applicant
        );
      } else {
        const individualCounts = new Map();
        patentData.forEach(d => {
          if (d.applicant.includes(';')) {
            const applicantsToCount = selectedAlphabet === 'ALL'
              ? d.applicant.split(';').map(a => a.trim()).filter(a => a)
              : d.applicant.split(';').map(a => a.trim()).filter(a => a && a.charAt(0).toUpperCase() === selectedAlphabet);
            applicantsToCount.forEach(app => {
              individualCounts.set(app, (individualCounts.get(app) || 0) + 1);
            });
          } else if (d.applicant) {
            if (selectedAlphabet === 'ALL' || d.applicant.charAt(0).toUpperCase() === selectedAlphabet) {
              individualCounts.set(d.applicant, (individualCounts.get(d.applicant) || 0) + 1);
            }
          }
        });
        applicantCounts = individualCounts;
      }

      const topData = Array.from(applicantCounts, ([name, value]) => ({ name, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, nSlider);

      if (topData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center">No data available for this letter</p>';
        return;
      }

      // Calculate total portfolio count based on countMode
      const totalPortfolio = countMode === 'original' 
        ? patentData.length 
        : Array.from(applicantCounts.values()).reduce((sum, val) => sum + val, 0);

      const width = container.clientWidth;
      const height = 600;

      const root = d3.hierarchy({ children: topData }).sum(d => d.value).sort((a, b) => b.value - a.value);
      d3.treemap().size([width, height]).padding(2)(root);

      const svg = d3.select(container).append('svg').attr('width', width).attr('height', height);
      
      // Get color palette from study configuration
      const corporateBlues = getStudyColors(selectedStudy, 'monochrome');

      const cell = svg.selectAll('g').data(root.leaves()).join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', (d, i) => corporateBlues[i % corporateBlues.length])
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Add text with word wrapping
      cell.each(function(d) {
        const cellWidth = d.x1 - d.x0;
        const cellHeight = d.y1 - d.y0;
        const textElement = d3.select(this).append('text')
          .attr('x', 4)
          .attr('y', 16)
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', 'white');

        // Word wrap the company name
        const words = d.data.name.split(/\s+/);
        let line = [];
        let lineNumber = 0;
        const lineHeight = 14;
        const maxLines = Math.floor((cellHeight - 25) / lineHeight);

        words.forEach((word, i) => {
          line.push(word);
          const testLine = line.join(' ');
          const testLength = testLine.length * 7; // Approximate character width

          if (testLength > cellWidth - 10 || i === words.length - 1) {
            if (lineNumber < maxLines) {
              textElement.append('tspan')
                .attr('x', 4)
                .attr('dy', lineNumber === 0 ? 0 : lineHeight)
                .text(testLine);
              lineNumber++;
              line = [];
            } else if (lineNumber === maxLines && i < words.length - 1) {
              // Truncate with ellipsis
              textElement.append('tspan')
                .attr('x', 4)
                .attr('dy', lineNumber === 0 ? 0 : lineHeight)
                .text(testLine.substring(0, Math.floor((cellWidth - 20) / 7)) + '...');
              lineNumber++;
              return;
            }
          }
        });
        
        // Add count text right below the company name
        const countYPosition = 16 + (lineNumber * lineHeight) + 5; // 5px spacing below name
        d3.select(this).append('text')
          .attr('x', 4)
          .attr('y', countYPosition)
          .text(`Count: ${d.data.value}`)
          .attr('font-size', '11px')
          .attr('fill', 'white');
      });

      // Remove any existing tooltips before creating new one
      d3.select('body').selectAll('.applicant-tooltip').remove();

      // Create custom tooltip div
      const tooltip = d3.select('body').append('div')
        .attr('class', 'applicant-tooltip')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      // Add hover events with trends and focus areas
      cell.on('mouseenter', function(event, d) {
        const applicantPatents = patentData.filter(p => {
          // Exact match first (handles both single and joint applicants)
          if (p.applicant === d.data.name) return true;
          // Check if patent has joint applicants and cell name is one of them
          if (p.applicant.includes(';')) {
            return p.applicant.split(';').map(a => a.trim()).includes(d.data.name);
          }
          return false;
        });

        // Classify as granted or applications
        let grantedCount = 0;
        let applicationsCount = 0;
        applicantPatents.forEach(p => {
          // Aggressive normalization: remove ALL whitespace
          const patentNo = (p.patentNo || '').replace(/\s+/g, '').replace(/[\-\/]/g, '').toUpperCase();
          const jurisdiction = patentNo.substring(0, 2);
          
          let isGranted = false;
          let isApplication = false;
          
          // All WO patents are applications
          if (jurisdiction === 'WO') {
            isApplication = true;
          } else {
            // Granted: ends with B (with or without number)
            if (patentNo.match(/B\d*$/)) {
              isGranted = true;
            }
            // Applications: ends with A followed by number (A1, A2, A3, A4, A9, etc.)
            else if (patentNo.match(/A\d+$/)) {
              isApplication = true;
            }
            // Special case: US ending with just A (no number) is granted
            else if (jurisdiction === 'US' && patentNo.endsWith('A')) {
              isGranted = true;
            }
          }
          
          if (isGranted) grantedCount++;
          if (isApplication) applicationsCount++;
        });
        
        const totalCount = applicantPatents.length;
        const grantedPct = totalCount > 0 ? ((grantedCount / totalCount) * 100).toFixed(1) : 0;
        const applicationsPct = totalCount > 0 ? ((applicationsCount / totalCount) * 100).toFixed(1) : 0;

        // Calculate 3-year filing trend
        const currentYear = new Date().getFullYear();
        const yearCounts = {};
        for (let i = 0; i < 3; i++) {
          yearCounts[currentYear - i] = 0;
        }
        
        applicantPatents.forEach(p => {
          if (p.filingDate) {
            const year = parseInt(p.filingDate.substring(0, 4));
            if (yearCounts[year] !== undefined) {
              yearCounts[year]++;
            }
          }
        });
        
        const trendText = Object.entries(yearCounts)
          .sort((a, b) => a[0] - b[0])
          .map(([year, count]) => `${year}: ${count}`)
          .join(', ');

        // Get top 3 L4 focus areas
        const l4Counts = {};
        applicantPatents.forEach(p => {
          const l4Value = p.l4 ? p.l4.trim() : '';
          if (l4Value) {
            l4Counts[l4Value] = (l4Counts[l4Value] || 0) + 1;
          }
        });
        
        const topL4 = Object.entries(l4Counts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([l4, count]) => `${l4} (${count})`)
          .join('\n');

        const portfolioPct = ((d.data.value / totalPortfolio) * 100).toFixed(1);
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nTotal Patents: ${d.data.value} (${portfolioPct}%)\n\n<strong>Granted:</strong> ${grantedCount} (${grantedPct}%)\n<strong>Applications:</strong> ${applicationsCount} (${applicationsPct}%)\n\n<strong>Filing Trend:</strong>\n${trendText}\n\n<strong>Main Focus Areas:</strong>\n${topL4 || 'No category data'}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });
    }
  }, [activeTab, view, patentData, selectedAlphabet, nSlider, countMode]);

  // Render Technology Segments Treemap
  useEffect(() => {
    if (activeTab === 'basic' && view === 'technology-segments' && patentData.length > 0) {
      const container = document.getElementById('technology-treemap');
      if (!container) return;

      d3.select(container).selectAll('*').remove();

      // Group by L4 (Problem Category)
      const l4Counts = d3.rollup(patentData.filter(d => d.l4), v => v.length, d => d.l4);
      const topData = Array.from(l4Counts, ([name, value]) => ({ 
        name, 
        value,
        patents: patentData.filter(d => d.l4 === name)
      }))
        .sort((a, b) => b.value - a.value)
        .slice(0, nSlider);

      if (topData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center">No technology segment data available</p>';
        return;
      }

      // Calculate total for percentage
      const totalPatents = patentData.filter(d => d.l4).length;

      const width = container.clientWidth;
      const height = 600;

      const root = d3.hierarchy({ children: topData }).sum(d => d.value).sort((a, b) => b.value - a.value);
      d3.treemap().size([width, height]).padding(2)(root);

      const svg = d3.select(container).append('svg').attr('width', width).attr('height', height);
      
      // Get color palette from study configuration
      const corporateBlues = getStudyColors(selectedStudy, 'monochrome');

      const cell = svg.selectAll('g').data(root.leaves()).join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', (d, i) => corporateBlues[i % corporateBlues.length])
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      cell.append('text')
        .attr('x', 4).attr('y', 20)
        .text(d => d.data.name)
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', 'white');

      cell.append('text')
        .attr('x', 4).attr('y', 33)
        .text(d => {
          const percentage = ((d.data.value / totalPatents) * 100).toFixed(1);
          return `Patents: ${d.data.value} (${percentage}%)`;
        })
        .attr('font-size', '11px')
        .attr('fill', 'white');

      // Create custom tooltip div
      const tooltip = d3.select('body').append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      // Add hover events with custom tooltip
      cell.on('mouseenter', function(event, d) {
        const patents = d.data.patents;
        
        // Classify as granted or applications
        let grantedCount = 0;
        let applicationsCount = 0;
        patents.forEach(p => {
          const suffix = p.patentNo.slice(-2);
          const isGranted = suffix === 'B1' || suffix === 'B2' || (p.patentNo.endsWith('A') && !p.patentNo.match(/A[129]$/));
          const isApplication = suffix === 'A1' || suffix === 'A2' || suffix === 'A9';
          
          if (isGranted) grantedCount++;
          if (isApplication) applicationsCount++;
        });
        
        const grantedPct = d.data.value > 0 ? ((grantedCount / d.data.value) * 100).toFixed(1) : 0;
        const applicationsPct = d.data.value > 0 ? ((applicationsCount / d.data.value) * 100).toFixed(1) : 0;
        
        // Top 3 applicants by true count
        const applicantCounts = new Map();
        patents.forEach(p => {
          if (p.applicant.includes(';')) {
            p.applicant.split(';').map(a => a.trim()).forEach(app => {
              applicantCounts.set(app, (applicantCounts.get(app) || 0) + 1);
            });
          } else if (p.applicant) {
            applicantCounts.set(p.applicant, (applicantCounts.get(p.applicant) || 0) + 1);
          }
        });
        
        const topApplicants = Array.from(applicantCounts, ([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3)
          .map(a => `${a.name} (${a.count})`)
          .join('\n');

        // Filing trend for last 3 years
        const currentYear = new Date().getFullYear();
        const yearCounts = {};
        for (let i = 0; i < 3; i++) {
          yearCounts[currentYear - i] = 0;
        }
        
        patents.forEach(p => {
          if (p.filingDate) {
            const year = parseInt(p.filingDate.substring(0, 4));
            if (yearCounts[year] !== undefined) {
              yearCounts[year]++;
            }
          }
        });
        
        const trendText = Object.entries(yearCounts)
          .sort((a, b) => a[0] - b[0])
          .map(([year, count]) => `${year}: ${count}`)
          .join(', ');

        const percentage = ((d.data.value / totalPatents) * 100).toFixed(1);
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nTotal Patents: ${d.data.value} (${percentage}%)\n\n<strong>Granted:</strong> ${grantedCount} (${grantedPct}%)\n<strong>Applications:</strong> ${applicationsCount} (${applicationsPct}%)\n\n<strong>Top Applicants:</strong>\n${topApplicants}\n\n<strong>Filing Trend:</strong>\n${trendText}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });
    }
  }, [activeTab, view, patentData, nSlider]);

  // Process collaboration data
  useEffect(() => {
    if (activeTab === 'basic' && view === 'collaborations' && patentData.length > 0) {
      const collabCounts = {};
      
      patentData.forEach(patent => {
        if (!patent.applicant) return;
        
        const applicants = patent.applicant.split(';').map(a => a.trim()).filter(a => a);
        const size = applicants.length;
        
        if (!collabCounts[size]) {
          collabCounts[size] = { total: 0, combinations: {} };
        }
        collabCounts[size].total++;
        
        // Sort applicants alphabetically to normalize combinations
        const sortedApplicants = [...applicants].sort();
        const key = sortedApplicants.join(' & ');
        
        if (!collabCounts[size].combinations[key]) {
          collabCounts[size].combinations[key] = 0;
        }
        collabCounts[size].combinations[key]++;
      });
      
      setCollaborationData(collabCounts);
    }
  }, [activeTab, view, patentData]);

  // Render L4 Treemap (R&D Focus) for Innovation tab
  useEffect(() => {
    if (activeTab === 'innovation' && view === 'rd-explorer' && !selectedL4 && patentData.length > 0) {
      const container = document.getElementById('l4-treemap');
      if (!container) return;

      d3.select(container).selectAll('*').remove();
      d3.select('body').selectAll('.rd-tooltip').remove();

      // Filter patent data using centralized filter function
      let filteredData = applyFilters(patentData.filter(d => d.l4), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2,
          patentNoSearch});

      const l4Counts = d3.rollup(filteredData, v => v.length, d => d.l4);
      const topData = Array.from(l4Counts, ([name, value]) => ({ 
        name, 
        value,
        l3List: (() => {
          const l3Map = {};
          filteredData.filter(d => d.l4 === name && d.l3).forEach(d => {
            const key = d.l3.trim().toLowerCase();
            if (!l3Map[key]) {
              l3Map[key] = { display: d.l3.trim(), count: 0 };
            }
            l3Map[key].count++;
          });
          return Object.values(l3Map)
            .sort((a,b) => b.count - a.count)
            .map(item => item.display);
        })()
      })).sort((a, b) => b.value - a.value);

      if (topData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center">No R&D Focus data available</p>';
        return;
      }

      const width = container.clientWidth;
      const height = 600;

      const root = d3.hierarchy({ children: topData }).sum(d => d.value).sort((a, b) => b.value - a.value);
      d3.treemap().size([width, height]).padding(2)(root);

      const svg = d3.select(container).append('svg').attr('width', width).attr('height', height);
      // Get color palette from study configuration
      const corporateBlues = getStudyColors(selectedStudy, 'monochrome');

      const cell = svg.selectAll('g').data(root.leaves()).join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`)
        .style('cursor', 'pointer')
        .on('click', (event, d) => setSelectedL4(d.data.name));

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', (d, i) => corporateBlues[i % corporateBlues.length])
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      cell.append('text')
        .attr('x', 4).attr('y', 20)
        .text(d => d.data.name)
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .attr('fill', 'white');

      cell.append('text')
        .attr('x', 4)
        .attr('y', d => Math.min(33, d.y1 - d.y0))
        .text(d => `Patents: ${d.data.value}`)
        .attr('font-size', '11px')
        .attr('fill', 'white');

      // Create custom tooltip div
      const tooltip = d3.select('body').append('div')
        .attr('class', 'rd-tooltip')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      // Add hover events with L3 data
      cell.on('mouseenter', function(event, d) {
        let content = `<strong>${d.data.name} contains:</strong>\n`;
        if (d.data.l3List && d.data.l3List.length > 0) {
          const top5 = d.data.l3List.slice(0, 5);
          content += top5.join(', ');
          if (d.data.l3List.length > 5) content += ' ...';
        } else {
          content += 'No L3 data';
        }
        content += `\n\nClick to see Technology Levers`;

        tooltip
          .style('opacity', 1)
          .html(content)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });
    }
  }, [activeTab, view, selectedL4, patentData, selectedApplicant, textSearch, selectedL2, selectedStudy, patentNoSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Render L9 Treemap (Technology Levers) for Innovation tab
  useEffect(() => {
    if (activeTab === 'innovation' && view === 'rd-explorer' && selectedL4 && patentData.length > 0) {
      const container = document.getElementById('l9-treemap');
      if (!container) return;

      d3.select(container).selectAll('*').remove();
      d3.select('body').selectAll('.rd-tooltip').remove();

      // Filter patents using centralized filter function
      let l4Patents = applyFilters(patentData.filter(d => d.l4 === selectedL4 && d.l7), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2,
          patentNoSearch});

      // Count L7 individually (split combos)
      const l7Counts = new Map();
      l4Patents.forEach(patent => {
        const l7Values = patent.l7.split(',').map(v => v.trim()).filter(v => v);
        l7Values.forEach(l7 => {
          l7Counts.set(l7, (l7Counts.get(l7) || 0) + 1);
        });
      });

      const topData = Array.from(l7Counts, ([name, value]) => ({ 
        name, 
        value,
        l6List: (() => {
          const l6Map = {};
          l4Patents.forEach(patent => {
            const l7Values = patent.l7.split(',').map(v => v.trim());
            if (l7Values.includes(name) && patent.l6) {
              const key = patent.l6.trim().toLowerCase();
              if (!l6Map[key]) {
                l6Map[key] = { display: patent.l6.trim(), count: 0 };
              }
              l6Map[key].count++;
            }
          });
          return Object.values(l6Map)
            .sort((a,b) => b.count - a.count)
            .map(item => item.display);
        })()
      })).sort((a, b) => b.value - a.value);

      if (topData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center">No Technology Levers data available for this R&D Focus</p>';
        return;
      }

      const width = container.clientWidth;
      const height = 600;

      const root = d3.hierarchy({ children: topData }).sum(d => d.value).sort((a, b) => b.value - a.value);
      d3.treemap().size([width, height]).padding(2)(root);

      const svg = d3.select(container).append('svg').attr('width', width).attr('height', height);
      // Get color palette from study configuration
      const corporateBlues = getStudyColors(selectedStudy, 'monochrome');

      const cell = svg.selectAll('g').data(root.leaves()).join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', (d, i) => corporateBlues[i % corporateBlues.length])
        .attr('stroke', 'white')
        .attr('stroke-width', 2);

      // Add text with word wrapping
      cell.each(function(d) {
        const cellWidth = d.x1 - d.x0;
        const cellHeight = d.y1 - d.y0;
        const textElement = d3.select(this).append('text')
          .attr('x', 4)
          .attr('y', 16)
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', 'white');

        const words = d.data.name.split(/\s+/);
        let line = [];
        let lineNumber = 0;
        const lineHeight = 14;
        const maxLines = Math.floor((cellHeight - 25) / lineHeight);

        words.forEach((word, i) => {
          line.push(word);
          const testLine = line.join(' ');
          const testLength = testLine.length * 7;

          if (testLength > cellWidth - 10 || i === words.length - 1) {
            if (lineNumber < maxLines) {
              textElement.append('tspan')
                .attr('x', 4)
                .attr('dy', lineNumber === 0 ? 0 : lineHeight)
                .text(testLine);
              lineNumber++;
              line = [];
            } else if (lineNumber === maxLines && i < words.length - 1) {
              textElement.append('tspan')
                .attr('x', 4)
                .attr('dy', lineNumber === 0 ? 0 : lineHeight)
                .text(testLine.substring(0, Math.floor((cellWidth - 20) / 7)) + '...');
              return;
            }
          }
        });
      });

      cell.append('text')
        .attr('x', 4)
        .attr('y', d => {
          const cellHeight = d.y1 - d.y0;
          // Position count below title, adjusting for cell height
          return Math.min(33, cellHeight - 20);
        })
        .text(d => `Patents: ${d.data.value}`)
        .attr('font-size', '11px')
        .attr('fill', 'white');

      // Create custom tooltip div
      const tooltip = d3.select('body').append('div')
        .attr('class', 'rd-tooltip')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      // Add hover events with L6 data
      cell.on('mouseenter', function(event, d) {
        let content = `<strong>${d.data.name} contains:</strong>\n`;
        if (d.data.l6List && d.data.l6List.length > 0) {
          const top5 = d.data.l6List.slice(0, 5);
          content += top5.join(', ');
          if (d.data.l6List.length > 5) content += ' ...';
        } else {
          content += 'No L6 data';
        }

        tooltip
          .style('opacity', 1)
          .html(content)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });
    }
  }, [activeTab, view, selectedL4, patentData, selectedApplicant, textSearch, selectedL2, selectedStudy, patentNoSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Render Whitespace Heatmap for Innovation tab
  useEffect(() => {
    if (activeTab === 'innovation' && view === 'whitespace' && patentData.length > 0) {
      const container = document.getElementById('whitespace-heatmap');
      if (!container) return;

      d3.select(container).selectAll('*').remove();

      // Filter patent data using centralized function
      let filteredData = applyFilters(patentData.filter(d => d.l4 && d.l7), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2,
          patentNoSearch});

      // Build L4 x L7 matrix with citation data
      const matrix = new Map();
      const citationMatrix = new Map(); // Track citations per cell
      const allL4s = new Set();
      const allL7s = new Set();

      filteredData.forEach(patent => {
        const l4 = patent.l4;
        const l7Values = patent.l7.split(',').map(v => v.trim()).filter(v => v);
        
        allL4s.add(l4);
        l7Values.forEach(l7 => {
          allL7s.add(l7);
          const key = `${l4}|||${l7}`;
          matrix.set(key, (matrix.get(key) || 0) + 1);
          
          // Track citation data for this cell
          if (!citationMatrix.has(key)) {
            citationMatrix.set(key, []);
          }
          citationMatrix.get(key).push(patent.citedBy || 0);
        });
      });

      // Check if citation data exists
      const citationDataExists = patentData.some(d => d.citedBy > 0 || d.citationsMade > 0);

      // Calculate average citation strength per cell
      const avgCitations = new Map();
      if (citationDataExists) {
        citationMatrix.forEach((citations, key) => {
          const avg = d3.mean(citations) || 0;
          avgCitations.set(key, avg);
        });
      }

      const l4Array = Array.from(allL4s).sort();
      const l7Array = Array.from(allL7s).sort();

      if (l4Array.length === 0 || l7Array.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center">No data available for heatmap</p>';
        return;
      }

      // Calculate dimensions - responsive but support up to 20 columns
      const containerWidth = container.clientWidth;
      const margin = { top: 150, right: 20, bottom: 100, left: 150 };
      
      // Fixed cell size to support up to 20 columns with scroll
      const cellWidth = 80;
      const cellHeight = 50;
      
      // Calculate total width needed (may exceed container width for scroll)
      const totalWidth = l7Array.length * cellWidth + margin.left + margin.right;
      const width = Math.max(containerWidth, totalWidth); // Allow overflow
      const height = l4Array.length * cellHeight + margin.top + margin.bottom;

      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Color scale - red shades
      const maxCount = d3.max(Array.from(matrix.values()));
      const colorScale = d3.scaleSequential(d3.interpolateReds).domain([0, maxCount]);

      // Create custom tooltip div
      const tooltip = d3.select('body').append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      // Create cells
      const maxAvgCitation = citationDataExists ? d3.max(Array.from(avgCitations.values())) : 0;
      
      l4Array.forEach((l4, i) => {
        l7Array.forEach((l7, j) => {
          const key = `${l4}|||${l7}`;
          const count = matrix.get(key) || 0;
          const avgCite = avgCitations.get(key) || 0;
          
          // Determine border width based on citation strength
          let borderWidth = 1;
          let borderColor = '#fff';
          if (citationDataExists && count > 0 && maxAvgCitation > 0) {
            const citationStrength = avgCite / maxAvgCitation;
            borderWidth = 1 + citationStrength * 4; // 1-5px border
            borderColor = '#1976D2'; // Blue border for high citations
          }
          
          const cell = g.append('rect')
            .attr('x', j * cellWidth)
            .attr('y', i * cellHeight)
            .attr('width', cellWidth)
            .attr('height', cellHeight)
            .attr('fill', count > 0 ? colorScale(count) : '#f5f5f5')
            .attr('stroke', borderColor)
            .attr('stroke-width', borderWidth)
            .style('cursor', count > 0 ? 'pointer' : 'default');

          // Add click event to show patents table
          if (count > 0) {
            cell.on('click', function() {
              setWhitespaceSelection({ l4, l7 });
            });
          }

          // Add hover events with top 3 applicants
          if (count > 0) {
            cell.on('mouseenter', function(event) {
              // Get patents for this L4 x L7 combination
              const cellPatents = filteredData.filter(p => {
                const l7Values = p.l7.split(',').map(v => v.trim());
                return p.l4 === l4 && l7Values.includes(l7);
              });

              // Classify as granted or applications
              let grantedCount = 0;
              let applicationsCount = 0;
              cellPatents.forEach(p => {
                const suffix = p.patentNo.slice(-2);
                const isGranted = suffix === 'B1' || suffix === 'B2' || (p.patentNo.endsWith('A') && !p.patentNo.match(/A[129]$/));
                const isApplication = suffix === 'A1' || suffix === 'A2' || suffix === 'A9';
                
                if (isGranted) grantedCount++;
                if (isApplication) applicationsCount++;
              });
              
              const grantedPct = count > 0 ? ((grantedCount / count) * 100).toFixed(1) : 0;
              const applicationsPct = count > 0 ? ((applicationsCount / count) * 100).toFixed(1) : 0;

              // Calculate top 3 applicants for this cell
              const applicantCounts = {};
              cellPatents.forEach(p => {
                if (p.applicant.includes(';')) {
                  p.applicant.split(';').map(a => a.trim()).forEach(app => {
                    applicantCounts[app] = (applicantCounts[app] || 0) + 1;
                  });
                } else {
                  applicantCounts[p.applicant] = (applicantCounts[p.applicant] || 0) + 1;
                }
              });
              
              const topApplicants = Object.entries(applicantCounts)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([app, count]) => `${app} (${count})`)
                .join('\n');

              tooltip
                .style('opacity', 1)
                .html(`<strong>${l4} × ${l7}</strong>\nPatents: ${count}${citationDataExists ? `\nAvg Citations: ${avgCite.toFixed(1)}` : ''}\n\n<strong>Granted:</strong> ${grantedCount} (${grantedPct}%)\n<strong>Applications:</strong> ${applicationsCount} (${applicationsPct}%)\n\n<strong>Top Applicants:</strong>\n${topApplicants || 'No applicant data'}`)
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 10) + 'px');
            })
            .on('mousemove', function(event) {
              tooltip
                .style('left', (event.pageX + 10) + 'px')
                .style('top', (event.pageY - 10) + 'px');
            })
            .on('mouseleave', function() {
              tooltip.style('opacity', 0);
            });
          }
          
          if (count > 0) {
            g.append('text')
              .attr('x', j * cellWidth + cellWidth / 2)
              .attr('y', i * cellHeight + cellHeight / 2)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('font-size', '14px')
              .attr('font-weight', 'bold')
              .attr('fill', count > maxCount * 0.5 ? 'white' : 'black')
              .text(count);
          }
        });
      });

      // L4 labels (rows) - word-wrapped
      l4Array.forEach((l4, i) => {
        const maxWidth = margin.left - 15;
        const words = l4.split(/\s+/);
        const lineHeight = 16;
        let lines = [];
        let currentLine = [];
        
        words.forEach(word => {
          currentLine.push(word);
          const testLine = currentLine.join(' ');
          if (testLine.length * 7 > maxWidth) {
            if (currentLine.length > 1) {
              currentLine.pop();
              lines.push(currentLine.join(' '));
              currentLine = [word];
            } else {
              lines.push(word);
              currentLine = [];
            }
          }
        });
        if (currentLine.length > 0) {
          lines.push(currentLine.join(' '));
        }
        
        const startY = i * cellHeight + cellHeight / 2 - ((lines.length - 1) * lineHeight) / 2;
        lines.forEach((line, idx) => {
          g.append('text')
            .attr('x', -10)
            .attr('y', startY + idx * lineHeight)
            .attr('text-anchor', 'end')
            .attr('dominant-baseline', 'middle')
            .attr('font-size', '14px')
            .text(line);
        });
      });

      // L7 labels (columns) - slanted at 45 degrees
      g.selectAll('.l7-label')
        .data(l7Array)
        .join('text')
        .attr('class', 'l7-label')
        .attr('x', (d, i) => i * cellWidth + cellWidth / 2)
        .attr('y', -10)
        .attr('text-anchor', 'start')
        .attr('transform', (d, i) => `rotate(-45 ${i * cellWidth + cellWidth / 2} -10)`)
        .attr('font-size', '14px')
        .text(d => d)
        .append('title')
        .text(d => d);

      // Add legend
      const legendWidth = 200;
      const legendHeight = 15;
      const legend = svg.append('g')
        .attr('transform', `translate(${margin.left}, ${height - margin.bottom + 30})`);

      const legendScale = d3.scaleLinear()
        .domain([0, maxCount])
        .range([0, legendWidth]);

      const legendAxis = d3.axisBottom(legendScale)
        .ticks(5)
        .tickFormat(d3.format('d'));

      // Legend gradient
      const defs = svg.append('defs');
      const gradient = defs.append('linearGradient')
        .attr('id', 'legend-gradient');

      gradient.selectAll('stop')
        .data(d3.range(0, 1.01, 0.1))
        .join('stop')
        .attr('offset', d => `${d * 100}%`)
        .attr('stop-color', d => colorScale(d * maxCount));

      legend.append('rect')
        .attr('width', legendWidth)
        .attr('height', legendHeight)
        .style('fill', 'url(#legend-gradient)');

      legend.append('g')
        .attr('transform', `translate(0, ${legendHeight})`)
        .call(legendAxis);

      legend.append('text')
        .attr('x', legendWidth / 2)
        .attr('y', -5)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .text('Patent Count');
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedL2, selectedStudy?.searchableFields, patentNoSearch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Compute Deep Dives combinations for selected L4
  useEffect(() => {
    if (activeTab === 'innovation' && view === 'deep-dives' && deepDivesL4 && patentData.length > 0) {
      // Filter patents using centralized function
      let filteredData = applyFilters(patentData.filter(d => d.l4 === deepDivesL4 && d.l7), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2,
          patentNoSearch});

      // Count L7 combinations
      const comboMap = new Map();
      filteredData.forEach(patent => {
        const combo = patent.l7; // Full combination as-is
        comboMap.set(combo, (comboMap.get(combo) || 0) + 1);
      });

      const combosArray = Array.from(comboMap, ([combination, count]) => ({ combination, count }))
        .sort((a, b) => b.count - a.count);

      setDeepDivesCombos(combosArray);
    } else if (view !== 'deep-dives') {
      setDeepDivesCombos([]);
    }
  }, [activeTab, view, deepDivesL4, patentData, selectedApplicant, textSearch, selectedL2, selectedStudy, patentNoSearch]);
  // eslint-disable-next-line react-hooks/exhaustive-deps

  // Helper: Calculate min-max scaled metrics per patent (portfolio-wide)
  const calculateScaledMetrics = (patents) => {
    // Portfolio-wide scaling
    const velocities = patents.map(p => {
      const age = new Date().getFullYear() - parseInt(p.filingDate?.substring(0, 4));
      return age > 0 ? (p.citedBy || 0) / age : 0;
    });
    const families = patents.map(p => p.familyCount || 1);
    const backwards = patents.map(p => p.citationsMade || 0);
    
    const ranges = {
      velocity: { min: Math.min(...velocities), max: Math.max(...velocities) },
      family: { min: Math.min(...families), max: Math.max(...families) },
      backward: { min: Math.min(...backwards), max: Math.max(...backwards) }
    };
    
    return patents.map((p, i) => ({
      ...p,
      velocityScore: ((velocities[i] - ranges.velocity.min) / (ranges.velocity.max - ranges.velocity.min || 1)) * 100,
      familyScore: ((families[i] - ranges.family.min) / (ranges.family.max - ranges.family.min || 1)) * 100,
      backwardScore: ((backwards[i] - ranges.backward.min) / (ranges.backward.max - ranges.backward.min || 1)) * 100
    }));
  };

  // Citations Tab: Impact Score View
  useEffect(() => {
    if (activeTab === 'citations' && view === 'impact-score' && patentData.length > 0) {
      const treemapContainer = document.getElementById('impact-l4-treemap');
      const tableBody = document.getElementById('impact-table-body');
      if (!treemapContainer || !tableBody) return;

      // Filter using centralized function
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});

      // Calculate impact scores
      const today = new Date();
      const patentsWithImpact = filteredData
        .filter(d => d.filingDate && d.citedBy > 0)
        .map(d => {
          const filingDate = new Date(d.filingDate);
          const ageInYears = (today - filingDate) / (365.25 * 24 * 60 * 60 * 1000);
          const impactScore = ageInYears > 0 ? d.citedBy / ageInYears : 0;
          return { ...d, ageInYears, impactScore };
        });

      if (patentsWithImpact.length === 0) {
        treemapContainer.innerHTML = '<p class="text-gray-600 text-center py-8">No citation data available</p>';
        tableBody.innerHTML = '<tr><td colspan="5" class="text-center py-4 text-gray-600">No data</td></tr>';
        return;
      }

      // Calculate threshold (75th percentile)
      const sortedScores = patentsWithImpact.map(d => d.impactScore).sort((a, b) => a - b);
      const threshold = d3.quantile(sortedScores, 0.75);

      // Update description with threshold
      const descriptionEl = document.getElementById('impact-description');
      if (descriptionEl) {
        descriptionEl.textContent = `Measures influence - Patents with no citations are excluded. Scores above threshold ${threshold.toFixed(2)} are more impactful`;
      }

      // L4 Treemap: Average impact by L4
      d3.select(treemapContainer).selectAll('*').remove();
      
      const l4Impact = d3.rollup(
        patentsWithImpact.filter(d => d.l4),
        v => ({
          count: v.length,
          avgImpact: d3.mean(v, d => d.impactScore)
        }),
        d => d.l4
      );

      const treemapData = Array.from(l4Impact, ([l4, stats]) => ({
        name: l4,
        value: stats.avgImpact,
        count: stats.count
      })).sort((a, b) => b.value - a.value).slice(0, 20);

      const width = treemapContainer.clientWidth;
      const height = 400;

      const root = d3.hierarchy({ children: treemapData })
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value);

      d3.treemap()
        .size([width, height])
        .padding(2)(root);

      const svg = d3.select(treemapContainer)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      // Get multicolor palette from study configuration
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));

      // Create custom tooltip
      const tooltip = d3.select('body').append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      const cell = svg.selectAll('g')
        .data(root.leaves())
        .join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', d => color(d.data.name))
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer');

      cell.append('text')
        .attr('x', 4)
        .attr('y', 16)
        .text(d => {
          const cellWidth = d.x1 - d.x0;
          if (cellWidth < 100) return ''; // Hide label if too small
          return d.data.name.length > 15 ? d.data.name.substring(0, 15) + '...' : d.data.name;
        })
        .style('font-size', '12px')
        .style('fill', 'white')
        .style('font-weight', 'bold');

      cell.append('text')
        .attr('x', 4)
        .attr('y', 32)
        .text(d => {
          const cellWidth = d.x1 - d.x0;
          if (cellWidth < 100) return ''; // Hide label if too small
          return `Impact: ${d.data.value.toFixed(2)}`;
        })
        .style('font-size', '10px')
        .style('fill', 'white');

      // Add tooltip hover events
      cell.on('mouseenter', function(event, d) {
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nAvg Impact Score: ${d.data.value.toFixed(2)}\nPatents: ${d.data.count}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });

      // High-Impact Patents Table (above threshold)
      const highImpactPatents = patentsWithImpact
        .filter(d => d.impactScore >= threshold)
        .sort((a, b) => b.impactScore - a.impactScore)
        .slice(0, 20);

      tableBody.innerHTML = highImpactPatents.map(d => `
        <tr class="border-t hover:bg-gray-50">
          <td class="px-4 py-2 text-sm">${d.patentNo}</td>
          <td class="px-4 py-2 text-sm">${toSentenceCase(d.title).substring(0, 60)}${d.title.length > 60 ? '...' : ''}</td>
          <td class="px-4 py-2 text-sm">${d.applicant || '-'}</td>
          <td class="px-4 py-2 text-sm">${d.l4 || '-'}</td>
          <td class="px-4 py-2 text-sm text-right font-semibold">${d.impactScore.toFixed(2)}</td>
        </tr>
      `).join('');
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy]);

  // Citations Tab: Velocity Scores View
  useEffect(() => {
    if (activeTab === 'citations' && view === 'velocity-score' && patentData.length > 0) {
      const treemapContainer = document.getElementById('velocity-l4-treemap');
      const tableBody = document.getElementById('velocity-table-body');
      if (!treemapContainer || !tableBody) return;

      // Filter using centralized function
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});

      // Calculate velocity scores - filter to recent patents only
      const today = new Date();
      const cutoffDate = new Date(today.getFullYear() - velocityYears, today.getMonth(), today.getDate());
      
      const recentPatents = filteredData.filter(d => {
        if (!d.filingDate) return false;
        const filingDate = new Date(d.filingDate);
        return filingDate >= cutoffDate;
      });

      const patentsWithVelocity = recentPatents
        .filter(d => d.citedBy > 0)
        .map(d => {
          const filingDate = new Date(d.filingDate);
          const ageInYears = (today - filingDate) / (365.25 * 24 * 60 * 60 * 1000);
          const velocityScore = ageInYears > 0 ? d.citedBy / ageInYears : 0;
          return { ...d, ageInYears, velocityScore };
        });

      if (patentsWithVelocity.length === 0) {
        treemapContainer.innerHTML = '<p class="text-gray-600 text-center py-8">Lack of data to predict velocity</p>';
        tableBody.innerHTML = '<tr><td colspan="5" class="text-center py-4 text-gray-600">Lack of data to predict velocity</td></tr>';
        return;
      }

      // Calculate threshold (75th percentile)
      const sortedScores = patentsWithVelocity.map(d => d.velocityScore).sort((a, b) => a - b);
      const threshold = d3.quantile(sortedScores, 0.75);

      // Update description with threshold
      const descriptionEl = document.getElementById('velocity-description');
      if (descriptionEl) {
        descriptionEl.textContent = `Measures momentum - Patents with no citations are excluded. Scores above threshold ${threshold.toFixed(2)} show rapid citation growth`;
      }

      // L4 Treemap: Average velocity by L4
      d3.select(treemapContainer).selectAll('*').remove();
      
      const l4Velocity = d3.rollup(
        patentsWithVelocity.filter(d => d.l4),
        v => ({
          count: v.length,
          avgVelocity: d3.mean(v, d => d.velocityScore)
        }),
        d => d.l4
      );

      const treemapData = Array.from(l4Velocity, ([l4, stats]) => ({
        name: l4,
        value: stats.avgVelocity,
        count: stats.count
      })).sort((a, b) => b.value - a.value);

      const width = treemapContainer.clientWidth;
      const height = 400;

      const root = d3.hierarchy({ children: treemapData })
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value);

      d3.treemap()
        .size([width, height])
        .padding(2)(root);

      const svg = d3.select(treemapContainer)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      // Get multicolor palette from study configuration
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));

      // Create custom tooltip
      const tooltip = d3.select('body').append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      const cell = svg.selectAll('g')
        .data(root.leaves())
        .join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', d => color(d.data.name))
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer');

      cell.append('text')
        .attr('x', 4)
        .attr('y', 16)
        .text(d => {
          const cellWidth = d.x1 - d.x0;
          if (cellWidth < 100) return '';
          return d.data.name.length > 15 ? d.data.name.substring(0, 15) + '...' : d.data.name;
        })
        .style('font-size', '12px')
        .style('fill', 'white')
        .style('font-weight', 'bold');

      cell.append('text')
        .attr('x', 4)
        .attr('y', 32)
        .text(d => {
          const cellWidth = d.x1 - d.x0;
          if (cellWidth < 100) return '';
          return `Velocity: ${d.data.value.toFixed(2)}`;
        })
        .style('font-size', '10px')
        .style('fill', 'white');

      // Add tooltip hover events
      cell.on('mouseenter', function(event, d) {
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nAvg Velocity Score: ${d.data.value.toFixed(2)}\nPatents: ${d.data.count}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });

      // High-Velocity Patents Table (above threshold)
      const highVelocityPatents = patentsWithVelocity
        .filter(d => d.velocityScore >= threshold)
        .sort((a, b) => b.velocityScore - a.velocityScore)
        .slice(0, 20);

      tableBody.innerHTML = highVelocityPatents.map(d => `
        <tr class="border-t hover:bg-gray-50">
          <td class="px-4 py-2 text-sm">${d.patentNo}</td>
          <td class="px-4 py-2 text-sm">${toSentenceCase(d.title).substring(0, 60)}${d.title.length > 60 ? '...' : ''}</td>
          <td class="px-4 py-2 text-sm">${d.applicant || '-'}</td>
          <td class="px-4 py-2 text-sm">${d.l4 || '-'}</td>
          <td class="px-4 py-2 text-sm text-right font-semibold">${d.velocityScore.toFixed(2)}</td>
        </tr>
      `).join('');
    }
  }, [activeTab, view, velocityYears, patentData, selectedApplicant, textSearch, selectedStudy]);

  // Citations Tab: Patent Strength Index View
  useEffect(() => {
    if (activeTab === 'citations' && view === 'strength-index' && patentData.length > 0) {
      const treemapContainer = document.getElementById('strength-l4-treemap');
      const tableBody = document.getElementById('strength-table-body');
      if (!treemapContainer || !tableBody) return;

      // Filter using centralized function
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});

      // Calculate scaled metrics and strength scores
      const scaledPatents = calculateScaledMetrics(filteredData);
      const patentsWithStrength = scaledPatents
        .filter(d => d.citedBy > 0 || d.familyCount > 0)
        .map(d => {
          const strengthScore = (d.velocityScore + d.familyScore + d.backwardScore) / 3;
          return { ...d, strengthScore };
        });

      if (patentsWithStrength.length === 0) {
        treemapContainer.innerHTML = '<p class="text-gray-600 text-center py-8">No citation data available</p>';
        tableBody.innerHTML = '<tr><td colspan="5" class="text-center py-4 text-gray-600">No data</td></tr>';
        return;
      }

      // Calculate threshold (75th percentile)
      const sortedScores = patentsWithStrength.map(d => d.strengthScore).sort((a, b) => a - b);
      const threshold = d3.quantile(sortedScores, 0.75);

      // Update description with threshold
      const descriptionEl = document.getElementById('strength-description');
      if (descriptionEl) {
        descriptionEl.textContent = `Measures current value using citation velocity, family size, and backward citations. Scores above ${threshold.toFixed(2)} indicate higher strength`;
      }

      // L4 Treemap: Average strength by L4
      d3.select(treemapContainer).selectAll('*').remove();
      
      const l4Strength = d3.rollup(
        patentsWithStrength.filter(d => d.l4),
        v => ({
          count: v.length,
          avgStrength: d3.mean(v, d => d.strengthScore)
        }),
        d => d.l4
      );

      const treemapData = Array.from(l4Strength, ([l4, stats]) => ({
        name: l4,
        value: stats.avgStrength,
        count: stats.count
      })).sort((a, b) => b.value - a.value);

      const width = treemapContainer.clientWidth;
      const height = 400;

      const root = d3.hierarchy({ children: treemapData })
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value);

      d3.treemap()
        .size([width, height])
        .padding(2)(root);

      const svg = d3.select(treemapContainer)
        .append('svg')
        .attr('width', width)
        .attr('height', height);

      // Get multicolor palette from study configuration
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));

      // Create custom tooltip
      const tooltip = d3.select('body').append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');

      const cell = svg.selectAll('g')
        .data(root.leaves())
        .join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);

      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', d => color(d.data.name))
        .attr('stroke', 'white')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer');

      cell.append('text')
        .attr('x', 4)
        .attr('y', 16)
        .text(d => {
          const cellWidth = d.x1 - d.x0;
          if (cellWidth < 100) return ''; // Hide label if too small
          return d.data.name.length > 15 ? d.data.name.substring(0, 15) + '...' : d.data.name;
        })
        .style('font-size', '12px')
        .style('fill', 'white')
        .style('font-weight', 'bold');

      cell.append('text')
        .attr('x', 4)
        .attr('y', 32)
        .text(d => {
          const cellWidth = d.x1 - d.x0;
          if (cellWidth < 100) return ''; // Hide label if too small
          return `Strength: ${d.data.value.toFixed(2)}`;
        })
        .style('font-size', '10px')
        .style('fill', 'white');

      // Add tooltip hover events
      cell.on('mouseenter', function(event, d) {
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nAvg Strength Score: ${d.data.value.toFixed(2)}\nPatents: ${d.data.count}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });

      // High-Strength Patents Table (above threshold)
      const highStrengthPatents = patentsWithStrength
        .filter(d => d.strengthScore >= threshold)
        .sort((a, b) => b.strengthScore - a.strengthScore)
        .slice(0, 20);

      tableBody.innerHTML = highStrengthPatents.map(d => `
        <tr class="border-t hover:bg-gray-50">
          <td class="px-4 py-2 text-sm">${d.patentNo}</td>
          <td class="px-4 py-2 text-sm">${toSentenceCase(d.title).substring(0, 60)}${d.title.length > 60 ? '...' : ''}</td>
          <td class="px-4 py-2 text-sm">${d.applicant || '-'}</td>
          <td class="px-4 py-2 text-sm">${d.l4 || '-'}</td>
          <td class="px-4 py-2 text-sm text-right font-semibold">${d.strengthScore.toFixed(2)}</td>
        </tr>
      `).join('');
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy]);

  // Citations Tab: Dense Navigators View
  useEffect(() => {
    if (activeTab === 'citations' && view === 'dense-navigators' && patentData.length > 0) {
      const treemapContainer = document.getElementById('navigator-l4-treemap');
      const tableBody = document.getElementById('navigator-table-body');
      if (!treemapContainer || !tableBody) return;

      // Filter using centralized function
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});

      const patentsWithBackward = filteredData.filter(d => d.citationsMade > 0);
      
      if (patentsWithBackward.length === 0) {
        treemapContainer.innerHTML = '<p class="text-gray-600 text-center py-8">No backward citation data</p>';
        tableBody.innerHTML = '<tr><td colspan="5" class="text-center py-4">No data</td></tr>';
        return;
      }

      const threshold = d3.quantile(patentsWithBackward.map(d => d.citationsMade).sort((a,b) => a-b), 0.75);
      const descEl = document.getElementById('navigator-description');
      if (descEl) descEl.textContent = `Patents with high backward citations build on extensive prior art. Scores above threshold ${Math.round(threshold)} are significant`;

      d3.select(treemapContainer).selectAll('*').remove();
      // Include ALL patents in L4 average (including zeros)
      const l4Data = d3.rollup(filteredData.filter(d => d.l4), v => ({count: v.length, avg: d3.mean(v, d => d.citationsMade || 0)}), d => d.l4);
      const data = Array.from(l4Data, ([l4, s]) => ({name: l4, value: s.avg, count: s.count})).sort((a,b) => b.value - a.value);
      
      const width = treemapContainer.clientWidth, height = 400;
      const root = d3.hierarchy({children: data}).sum(d => d.value).sort((a,b) => b.value - a.value);
      d3.treemap().size([width, height]).padding(2)(root);
      const svg = d3.select(treemapContainer).append('svg').attr('width', width).attr('height', height);
      // Get multicolor palette from study configuration
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));
      
      // Create custom tooltip
      const tooltip = d3.select('body').append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '10px')
        .style('border-radius', '5px')
        .style('font-size', '12px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');
      
      const cell = svg.selectAll('g').data(root.leaves()).join('g').attr('transform', d => `translate(${d.x0},${d.y0})`);
      
      cell.append('rect')
        .attr('width', d => d.x1-d.x0)
        .attr('height', d => d.y1-d.y0)
        .attr('fill', d => color(d.data.name))
        .attr('stroke', 'white')
        .attr('stroke-width', 2);
      
      cell.append('text')
        .attr('x', 4)
        .attr('y', 16)
        .text(d => d.x1-d.x0 < 100 ? '' : (d.data.name.length > 15 ? d.data.name.substring(0,15)+'...' : d.data.name))
        .style('font-size', '12px')
        .style('fill', 'white')
        .style('font-weight', 'bold');
      
      cell.append('text')
        .attr('x', 4)
        .attr('y', 32)
        .text(d => d.x1-d.x0 < 100 ? '' : `Backward: ${d.data.value.toFixed(1)}`)
        .style('font-size', '10px')
        .style('fill', 'white');
      
      // Add tooltip hover events
      cell.on('mouseenter', function(event, d) {
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nAvg Backward Citations: ${d.data.value.toFixed(1)}\nPatents: ${d.data.count}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });

      tableBody.innerHTML = patentsWithBackward.filter(d => d.citationsMade >= threshold).sort((a,b) => b.citationsMade - a.citationsMade).slice(0,20).map(d => `<tr class="border-t hover:bg-gray-50"><td class="px-4 py-2 text-sm">${d.patentNo}</td><td class="px-4 py-2 text-sm">${toSentenceCase(d.title).substring(0,60)}${d.title.length>60?'...':''}</td><td class="px-4 py-2 text-sm">${d.applicant||'-'}</td><td class="px-4 py-2 text-sm">${d.l4||'-'}</td><td class="px-4 py-2 text-sm text-right font-semibold">${d.citationsMade}</td></tr>`).join('');
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy]);

  // Trends Chart 1: Patents over time
  useEffect(() => {
    if (activeTab === 'trends' && view === 'filing-trends' && patentData.length > 0) {
      const container = document.getElementById('trends-chart-1');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Filter data using ribbon filters and centralized function
      let filteredData = applyFilters(patentData.filter(d => d[trendsDateField]), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      
      // Apply jurisdiction filter
      if (trendsJurisdiction !== 'ALL') {
        filteredData = filteredData.filter(d => {
          if (!d.patentNo) return false;
          const jurisdiction = d.patentNo.substring(0, 2).toUpperCase();
          return jurisdiction === trendsJurisdiction;
        });
      }
      
      // Apply view-specific filters based on grouping
      if (filingTrendsGroupBy === 'rd') {
        if (trendsL4) {
          filteredData = filteredData.filter(d => d.l4 === trendsL4);
        } else {
          filteredData = filteredData.filter(d => d.l4);
        }
      } else if (filingTrendsGroupBy === 'tech') {
        if (trendsL7) {
          filteredData = filteredData.filter(d => {
            if (!d.l7) return false;
            const l7Values = d.l7.split(',').map(v => v.trim());
            return l7Values.includes(trendsL7);
          });
        } else {
          filteredData = filteredData.filter(d => d.l7);
        }
      }
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No data available</p>';
        return;
      }
      
      // Group by year
      const yearCounts = d3.rollup(
        filteredData,
        v => v.length,
        d => parseInt(d[trendsDateField].substring(0, 4))
      );
      
      const data = Array.from(yearCounts, ([year, count]) => ({ year, count }))
        .sort((a, b) => a.year - b.year);
      
      // Chart dimensions
      const margin = { top: 20, right: 30, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const x = d3.scaleLinear()
        .domain(d3.extent(data, d => d.year))
        .range([0, width]);
      
      const maxCount = d3.max(data, d => d.count);
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      // Smart step size for y-axis
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Line with smooth curve
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      svg.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', getStudyColors(selectedStudy, 'multicolor')[0])
        .attr('stroke-width', 2)
        .attr('d', line);
      
      // Points
      svg.selectAll('circle')
        .data(data)
        .join('circle')
        .attr('cx', d => x(d.year))
        .attr('cy', d => y(d.count))
        .attr('r', 4)
        .attr('fill', getStudyColors(selectedStudy, 'multicolor')[0])
        .append('title')
        .text(d => `${d.year}: ${d.count} patents`);
      
      // Labels
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height + 40)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Year');
      
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Patent Count');
      
      // Title
      let title = 'Patents over time';
      if (filingTrendsGroupBy === 'overall') {
        title = 'Patents over time';
      } else if (filingTrendsGroupBy === 'rd') {
        title = trendsL4 ? `${trendsL4} - Patents over time` : 'R&D View - All Patents over time';
      } else if (filingTrendsGroupBy === 'tech') {
        title = trendsL7 ? `${trendsL7} - Patents over time` : 'Tech View - All Patents over time';
      }
      
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -5)
        .attr('text-anchor', 'middle')
        .style('font-size', '18px')
        .style('font-weight', 'bold')
        .text(title);
    }
  }, [activeTab, patentData, view, selectedApplicant, textSearch, trendsDateField, selectedStudy, trendsL4, trendsL7, filingTrendsGroupBy, trendsJurisdiction]);

  // Trends Chart 2: Top Applicants Over Time (All views)
  useEffect(() => {
    if (activeTab === 'trends' && view === 'filing-trends' && patentData.length > 0) {
      const container = document.getElementById('trends-chart-2');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Filter data using ribbon filters
      let filteredData = applyFilters(patentData.filter(d => d[trendsDateField] && d.applicant), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      
      // Apply jurisdiction filter
      if (trendsJurisdiction !== 'ALL') {
        filteredData = filteredData.filter(d => {
          if (!d.patentNo) return false;
          const jurisdiction = d.patentNo.substring(0, 2).toUpperCase();
          return jurisdiction === trendsJurisdiction;
        });
      }
      
      // Apply L4 filter when specific L4 is selected
      if (filingTrendsGroupBy === 'rd' && trendsL4) {
        filteredData = filteredData.filter(d => d.l4 === trendsL4);
      }
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No data available</p>';
        return;
      }
      
      // Apply count mode
      let processedData = filteredData;
      if (countMode === 'individual') {
        processedData = [];
        filteredData.forEach(patent => {
          const applicants = patent.applicant.split(';').map(a => a.trim()).filter(a => a);
          applicants.forEach(app => {
            processedData.push({ ...patent, applicant: app });
          });
        });
      }
      
      // Top applicants by year
      const applicantsByYear = d3.rollup(
        processedData,
        v => d3.rollup(v, vv => vv.length, d => d.applicant),
        d => parseInt(d[trendsDateField].substring(0, 4))
      );
      
      const topApplicants = Array.from(
        d3.rollup(processedData, v => v.length, d => d.applicant),
        ([name, count]) => ({ name, count })
      ).sort((a, b) => b.count - a.count).slice(0, 5).map(d => d.name);
      
      const years = Array.from(applicantsByYear.keys()).sort();
      
      const seriesData = topApplicants.map(app => ({
        name: app,
        values: years.map(year => ({
          year,
          count: applicantsByYear.get(year)?.get(app) || 0
        }))
      }));
      
      if (seriesData.length === 0 || seriesData[0].values.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">Insufficient data</p>';
        return;
      }
      
      // Chart dimensions
      const margin = { top: 40, right: 150, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const allYears = seriesData[0].values.map(d => d.year);
      const x = d3.scaleLinear()
        .domain(d3.extent(allYears))
        .range([0, width]);
      
      const maxCount = d3.max(seriesData, s => d3.max(s.values, d => d.count));
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Lines
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      // Symbol types
      const symbolTypes = [
        { type: d3.symbolCircle, filled: true },
        { type: d3.symbolSquare, filled: true },
        { type: d3.symbolTriangle, filled: true },
        { type: d3.symbolDiamond, filled: true },
        { type: d3.symbolCross, filled: true },
        { type: d3.symbolCircle, filled: false },
        { type: d3.symbolSquare, filled: false },
        { type: d3.symbolTriangle, filled: false },
        { type: d3.symbolDiamond, filled: false }
      ];
      
      const visibleSeries = new Set(seriesData.map(s => s.name));
      
      seriesData.forEach((series, seriesIndex) => {
        const lineGroup = svg.append('g')
          .attr('class', `line-group-${seriesIndex}`);
        
        lineGroup.append('path')
          .datum(series.values)
          .attr('class', `line-${seriesIndex}`)
          .attr('fill', 'none')
          .attr('stroke', color(series.name))
          .attr('stroke-width', 2)
          .attr('d', line);
        
        // Points with symbols
        const symbolDef = symbolTypes[seriesIndex % symbolTypes.length];
        const symbolGenerator = d3.symbol().type(symbolDef.type).size(64);
        
        lineGroup.append('g')
          .attr('class', `symbols-${seriesIndex}`)
          .selectAll('path')
          .data(series.values)
          .join('path')
          .attr('transform', d => `translate(${x(d.year)},${y(d.count)})`)
          .attr('d', symbolGenerator)
          .attr('fill', symbolDef.filled ? color(series.name) : 'white')
          .attr('stroke', color(series.name))
          .attr('stroke-width', symbolDef.filled ? 0 : 2)
          .append('title')
          .text(d => `${series.name}\n${d.year}: ${d.count} patents`);
      });
      
      // Legend with interactive toggle
      const legend = svg.append('g')
        .attr('transform', `translate(${width + 10}, 0)`);
      
      seriesData.forEach((series, i) => {
        const g = legend.append('g')
          .attr('transform', `translate(0, ${i * 20})`)
          .attr('class', `legend-item-${i}`)
          .classed('legend-item-visible', true)
          .on('click', function() {
            const isVisible = visibleSeries.has(series.name);
            
            if (isVisible) {
              visibleSeries.delete(series.name);
            } else {
              visibleSeries.add(series.name);
            }
            
            svg.select(`.line-${i}`)
              .classed('chart-line-visible', !isVisible)
              .classed('chart-line-hidden', isVisible);
            
            svg.select(`.symbols-${i}`)
              .classed('chart-line-visible', !isVisible)
              .classed('chart-line-hidden', isVisible);
            
            d3.select(this).select('text')
              .classed('legend-text-visible', !isVisible)
              .classed('legend-text-hidden', isVisible);
            
            d3.select(this).select('path, rect')
              .classed('legend-symbol-visible', !isVisible)
              .classed('legend-symbol-hidden', isVisible);
          });
        
        const symbolDef = symbolTypes[i % symbolTypes.length];
        const symbolGenerator = d3.symbol().type(symbolDef.type).size(64);
        
        g.append('path')
          .attr('transform', 'translate(7.5, 7.5)')
          .attr('d', symbolGenerator)
          .attr('fill', symbolDef.filled ? color(series.name) : 'white')
          .attr('stroke', color(series.name))
          .attr('stroke-width', symbolDef.filled ? 0 : 2);
        
        g.append('text')
          .attr('x', 20)
          .attr('y', 12)
          .style('font-size', '11px')
          .text(series.name.length > 20 ? series.name.substring(0, 20) + '...' : series.name);
      });
      
      // Labels
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height + 40)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Year');
      
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Patent Count');
      
      // Title
      const chartTitle = filingTrendsGroupBy === 'rd' && trendsL4 
        ? `${trendsL4} - Applicants over time`
        : 'Applicants over time';
      
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -20)
        .attr('text-anchor', 'middle')
        .style('font-size', '18px')
        .style('font-weight', 'bold')
        .text(chartTitle);
    }
  }, [activeTab, patentData, view, selectedApplicant, textSearch, trendsDateField, selectedStudy, trendsJurisdiction, filingTrendsGroupBy, countMode, trendsL4]);

  // Trends Chart 3: Distribution over time (varies by view)
  useEffect(() => {
    if (activeTab === 'trends' && view === 'filing-trends' && patentData.length > 0) {
      const container = document.getElementById('trends-chart-3');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Filter data using ribbon filters and centralized function
      let filteredData = applyFilters(patentData.filter(d => d[trendsDateField]), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      
      // Apply jurisdiction filter
      if (trendsJurisdiction !== 'ALL') {
        filteredData = filteredData.filter(d => {
          if (!d.patentNo) return false;
          const jurisdiction = d.patentNo.substring(0, 2).toUpperCase();
          return jurisdiction === trendsJurisdiction;
        });
      }
      
      // View-specific filtering
      if (filingTrendsGroupBy === 'overall') {
        filteredData = filteredData.filter(d => d.l4);
      } else if (filingTrendsGroupBy === 'rd') {
        if (trendsL4) {
          filteredData = filteredData.filter(d => d.l4 === trendsL4);
        } else {
          filteredData = filteredData.filter(d => d.l4);
        }
      } else if (filingTrendsGroupBy === 'tech') {
        if (trendsL7) {
          filteredData = filteredData.filter(d => {
            if (!d.l7) return false;
            const l7Values = d.l7.split(',').map(v => v.trim());
            return l7Values.includes(trendsL7);
          });
        } else {
          filteredData = filteredData.filter(d => d.l7);
        }
      }
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No data available</p>';
        return;
      }
      
      // Prepare data based on view
      let seriesData = [];
      let title = '';
      
      if (filingTrendsGroupBy === 'overall') {
        // L4 distribution over time
        const l4sByYear = d3.rollup(
          filteredData,
          v => d3.rollup(v, vv => vv.length, d => d.l4),
          d => parseInt(d[trendsDateField].substring(0, 4))
        );
        
        const allL4s = Array.from(new Set(filteredData.map(d => d.l4))).sort().slice(0, 5);
        const years = Array.from(l4sByYear.keys()).sort();
        
        seriesData = allL4s.map(l4 => ({
          name: l4,
          values: years.map(year => ({
            year,
            count: l4sByYear.get(year)?.get(l4) || 0
          }))
        }));
        
        title = 'R&D focus over time';
      } else if (filingTrendsGroupBy === 'rd') {
        // Apply count mode for applicants
        let processedData = filteredData;
        if (countMode === 'individual') {
          processedData = [];
          filteredData.forEach(patent => {
            const applicants = patent.applicant.split(';').map(a => a.trim()).filter(a => a);
            applicants.forEach(app => {
              processedData.push({ ...patent, applicant: app });
            });
          });
        }
        
        // Top applicants over time
        const applicantsByYear = d3.rollup(
          processedData,
          v => d3.rollup(v, vv => vv.length, d => d.applicant),
          d => parseInt(d[trendsDateField].substring(0, 4))
        );
        
        const topApplicants = Array.from(
          d3.rollup(processedData, v => v.length, d => d.applicant),
          ([name, count]) => ({ name, count })
        ).sort((a, b) => b.count - a.count).slice(0, 5).map(d => d.name);
        
        const years = Array.from(applicantsByYear.keys()).sort();
        
        seriesData = topApplicants.map(app => ({
          name: app,
          values: years.map(year => ({
            year,
            count: applicantsByYear.get(year)?.get(app) || 0
          }))
        }));
        
        title = trendsL4 ? `${trendsL4} - Applicants over time` : 'R&D View - All Applicants over time';
      } else if (filingTrendsGroupBy === 'tech') {
        // Apply count mode for applicants
        let processedData = filteredData;
        if (countMode === 'individual') {
          processedData = [];
          filteredData.forEach(patent => {
            const applicants = patent.applicant.split(';').map(a => a.trim()).filter(a => a);
            applicants.forEach(app => {
              processedData.push({ ...patent, applicant: app });
            });
          });
        }
        
        // Top applicants over time
        const applicantsByYear = d3.rollup(
          processedData,
          v => d3.rollup(v, vv => vv.length, d => d.applicant),
          d => parseInt(d[trendsDateField].substring(0, 4))
        );
        
        const topApplicants = Array.from(
          d3.rollup(processedData, v => v.length, d => d.applicant),
          ([name, count]) => ({ name, count })
        ).sort((a, b) => b.count - a.count).slice(0, 5).map(d => d.name);
        
        const years = Array.from(applicantsByYear.keys()).sort();
        
        seriesData = topApplicants.map(app => ({
          name: app,
          values: years.map(year => ({
            year,
            count: applicantsByYear.get(year)?.get(app) || 0
          }))
        }));
        
        title = trendsL7 ? `${trendsL7} - Applicants over time` : 'Tech View - All Applicants over time';
      }
      
      if (seriesData.length === 0 || seriesData[0].values.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">Insufficient data</p>';
        return;
      }
      
      // Chart dimensions
      const margin = { top: 40, right: 150, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const allYears = seriesData[0].values.map(d => d.year);
      const x = d3.scaleLinear()
        .domain(d3.extent(allYears))
        .range([0, width]);
      
      const maxCount = d3.max(seriesData, s => d3.max(s.values, d => d.count));
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      
      // Smart step size for y-axis
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      // Get multicolor palette from study configuration
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Lines
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      // Define symbol types (9 total: 5 filled + 4 open)
      const symbolTypes = [
        { type: d3.symbolCircle, filled: true },      // ● Circle (filled)
        { type: d3.symbolSquare, filled: true },      // ■ Square (filled)
        { type: d3.symbolTriangle, filled: true },    // ▲ Triangle (filled)
        { type: d3.symbolDiamond, filled: true },     // ◆ Diamond (filled)
        { type: d3.symbolCross, filled: true },       // ✕ Cross (filled)
        { type: d3.symbolCircle, filled: false },     // ○ Circle (open)
        { type: d3.symbolSquare, filled: false },     // □ Square (open)
        { type: d3.symbolTriangle, filled: false },   // △ Triangle (open)
        { type: d3.symbolDiamond, filled: false }     // ◇ Diamond (open)
      ];
      
      // Track visible series (all visible by default)
      const visibleSeries = new Set(seriesData.map(s => s.name));
      
      seriesData.forEach((series, seriesIndex) => {
        const lineGroup = svg.append('g')
          .attr('class', `line-group-${seriesIndex}`);
        
        lineGroup.append('path')
          .datum(series.values)
          .attr('class', `line-${seriesIndex}`)
          .attr('fill', 'none')
          .attr('stroke', color(series.name))
          .attr('stroke-width', 2)
          .attr('d', line);
        
        // Points with symbols
        const symbolDef = symbolTypes[seriesIndex % symbolTypes.length];
        const symbolGenerator = d3.symbol().type(symbolDef.type).size(64);
        
        lineGroup.append('g')
          .attr('class', `symbols-${seriesIndex}`)
          .selectAll('path')
          .data(series.values)
          .join('path')
          .attr('transform', d => `translate(${x(d.year)},${y(d.count)})`)
          .attr('d', symbolGenerator)
          .attr('fill', symbolDef.filled ? color(series.name) : 'white')
          .attr('stroke', color(series.name))
          .attr('stroke-width', symbolDef.filled ? 0 : 2)
          .append('title')
          .text(d => `${series.name}\n${d.year}: ${d.count} patents`);
      });
      
      // Legend with interactive toggle
      const legend = svg.append('g')
        .attr('transform', `translate(${width + 10}, 0)`);
      
      seriesData.forEach((series, i) => {
        const g = legend.append('g')
          .attr('transform', `translate(0, ${i * 20})`)
          .attr('class', `legend-item-${i}`)
          .classed('legend-item-visible', true)
          .on('click', function() {
            // Toggle visibility
            const isVisible = visibleSeries.has(series.name);
            
            if (isVisible) {
              visibleSeries.delete(series.name);
            } else {
              visibleSeries.add(series.name);
            }
            
            // Update line classes
            svg.select(`.line-${i}`)
              .classed('chart-line-visible', !isVisible)
              .classed('chart-line-hidden', isVisible);
            
            svg.select(`.symbols-${i}`)
              .classed('chart-line-visible', !isVisible)
              .classed('chart-line-hidden', isVisible);
            
            // Update legend classes
            d3.select(this).select('text')
              .classed('legend-text-visible', !isVisible)
              .classed('legend-text-hidden', isVisible);
            
            d3.select(this).select('path, rect')
              .classed('legend-symbol-visible', !isVisible)
              .classed('legend-symbol-hidden', isVisible);
          });
        
        // Show symbol in legend
        const symbolDef = symbolTypes[i % symbolTypes.length];
        const symbolGenerator = d3.symbol().type(symbolDef.type).size(64);
        
        g.append('path')
          .attr('transform', 'translate(7.5, 7.5)')
          .attr('d', symbolGenerator)
          .attr('fill', symbolDef.filled ? color(series.name) : 'white')
          .attr('stroke', color(series.name))
          .attr('stroke-width', symbolDef.filled ? 0 : 2);
        
        g.append('text')
          .attr('x', 20)
          .attr('y', 12)
          .style('font-size', '11px')
          .text(series.name.length > 20 ? series.name.substring(0, 20) + '...' : series.name);
      });
      
      // Labels
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height + 40)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Year');
      
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Patent Count');
      
      // Title
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -20)
        .attr('text-anchor', 'middle')
        .style('font-size', '18px')
        .style('font-weight', 'bold')
        .text(title);
    }
  }, [activeTab, patentData, view, selectedApplicant, textSearch, trendsDateField, selectedStudy, trendsL4, trendsL7, filingTrendsGroupBy, trendsJurisdiction, countMode]);

  // Trends Chart 4: Third distribution over time (varies by view)
  useEffect(() => {
    if (activeTab === 'trends' && view === 'filing-trends' && patentData.length > 0) {
      const container = document.getElementById('trends-chart-4');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Filter data using centralized function
      let filteredData = applyFilters(patentData.filter(d => d[trendsDateField]), {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      
      // Apply jurisdiction filter
      if (trendsJurisdiction !== 'ALL') {
        filteredData = filteredData.filter(d => {
          if (!d.patentNo) return false;
          const jurisdiction = d.patentNo.substring(0, 2).toUpperCase();
          return jurisdiction === trendsJurisdiction;
        });
      }
      
      // View-specific filtering
      if (filingTrendsGroupBy === 'overall') {
        filteredData = filteredData.filter(d => d.l7);
      } else if (filingTrendsGroupBy === 'rd') {
        if (trendsL4) {
          filteredData = filteredData.filter(d => d.l4 === trendsL4 && d.l7);
        } else {
          filteredData = filteredData.filter(d => d.l4 && d.l7);
        }
      } else if (filingTrendsGroupBy === 'tech') {
        if (trendsL7) {
          filteredData = filteredData.filter(d => {
            if (!d.l7 || !d.l4) return false;
            const l7Values = d.l7.split(',').map(v => v.trim());
            return l7Values.includes(trendsL7);
          });
        } else {
          filteredData = filteredData.filter(d => d.l7 && d.l4);
        }
      }
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No data available</p>';
        return;
      }
      
      // Prepare data based on view
      let seriesData = [];
      let title = '';
      
      if (filingTrendsGroupBy === 'overall') {
        // L7 usage over time
        const l7CountsByYear = new Map();
        
        filteredData.forEach(d => {
          const year = parseInt(d[trendsDateField].substring(0, 4));
          const l7Values = d.l7.split(',').map(v => v.trim());
          
          l7Values.forEach(l7 => {
            if (!l7CountsByYear.has(year)) {
              l7CountsByYear.set(year, new Map());
            }
            const yearMap = l7CountsByYear.get(year);
            yearMap.set(l7, (yearMap.get(l7) || 0) + 1);
          });
        });
        
        // Get top 5 L7s overall
        const l7Totals = new Map();
        filteredData.forEach(d => {
          const l7Values = d.l7.split(',').map(v => v.trim());
          l7Values.forEach(l7 => {
            l7Totals.set(l7, (l7Totals.get(l7) || 0) + 1);
          });
        });
        
        const topL7s = Array.from(l7Totals, ([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count).slice(0, 5).map(d => d.name);
        
        const years = Array.from(l7CountsByYear.keys()).sort();
        
        seriesData = topL7s.map(l7 => ({
          name: l7,
          values: years.map(year => ({
            year,
            count: l7CountsByYear.get(year)?.get(l7) || 0
          }))
        }));
        
        title = 'Tech Levers over time';
      } else if (filingTrendsGroupBy === 'rd') {
        // L7 levers used over time
        const l7CountsByYear = new Map();
        
        filteredData.forEach(d => {
          const year = parseInt(d[trendsDateField].substring(0, 4));
          const l7Values = d.l7.split(',').map(v => v.trim());
          
          l7Values.forEach(l7 => {
            if (!l7CountsByYear.has(year)) {
              l7CountsByYear.set(year, new Map());
            }
            const yearMap = l7CountsByYear.get(year);
            yearMap.set(l7, (yearMap.get(l7) || 0) + 1);
          });
        });
        
        // Get top 5 L7s overall
        const l7Totals = new Map();
        filteredData.forEach(d => {
          const l7Values = d.l7.split(',').map(v => v.trim());
          l7Values.forEach(l7 => {
            l7Totals.set(l7, (l7Totals.get(l7) || 0) + 1);
          });
        });
        
        const topL7s = Array.from(l7Totals, ([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count).slice(0, 5).map(d => d.name);
        
        const years = Array.from(l7CountsByYear.keys()).sort();
        
        seriesData = topL7s.map(l7 => ({
          name: l7,
          values: years.map(year => ({
            year,
            count: l7CountsByYear.get(year)?.get(l7) || 0
          }))
        }));
        
        title = trendsL4 ? `${trendsL4} - Tech Levers over time` : 'R&D View - All Tech Levers over time';
      } else if (filingTrendsGroupBy === 'tech') {
        // L4 areas over time
        const l4sByYear = d3.rollup(
          filteredData,
          v => d3.rollup(v, vv => vv.length, d => d.l4),
          d => parseInt(d[trendsDateField].substring(0, 4))
        );
        
        const topL4s = Array.from(
          d3.rollup(filteredData, v => v.length, d => d.l4),
          ([name, count]) => ({ name, count })
        ).sort((a, b) => b.count - a.count).slice(0, 5).map(d => d.name);
        
        const years = Array.from(l4sByYear.keys()).sort();
        
        seriesData = topL4s.map(l4 => ({
          name: l4,
          values: years.map(year => ({
            year,
            count: l4sByYear.get(year)?.get(l4) || 0
          }))
        }));
        
        title = trendsL7 ? `${trendsL7} - R&D focus over time` : 'Tech View - All R&D focus over time';
      }
      
      if (seriesData.length === 0 || seriesData[0].values.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">Insufficient data</p>';
        return;
      }
      
      // Chart dimensions
      const margin = { top: 40, right: 150, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const allYears = seriesData[0].values.map(d => d.year);
      const x = d3.scaleLinear()
        .domain(d3.extent(allYears))
        .range([0, width]);
      
      const maxCount = d3.max(seriesData, s => d3.max(s.values, d => d.count));
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      
      // Smart step size for y-axis
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      // Get multicolor palette from study configuration
      const color = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Lines
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      // Define symbol types (9 total: 5 filled + 4 open)
      const symbolTypes = [
        { type: d3.symbolCircle, filled: true },
        { type: d3.symbolSquare, filled: true },
        { type: d3.symbolTriangle, filled: true },
        { type: d3.symbolDiamond, filled: true },
        { type: d3.symbolCross, filled: true },
        { type: d3.symbolCircle, filled: false },
        { type: d3.symbolSquare, filled: false },
        { type: d3.symbolTriangle, filled: false },
        { type: d3.symbolDiamond, filled: false }
      ];
      
      // Track visible series (all visible by default)
      const visibleSeries = new Set(seriesData.map(s => s.name));
      
      seriesData.forEach((series, seriesIndex) => {
        const lineGroup = svg.append('g')
          .attr('class', `line-group-${seriesIndex}`);
        
        lineGroup.append('path')
          .datum(series.values)
          .attr('class', `line-${seriesIndex}`)
          .attr('fill', 'none')
          .attr('stroke', color(series.name))
          .attr('stroke-width', 2)
          .attr('d', line);
        
        // Points with symbols
        const symbolDef = symbolTypes[seriesIndex % symbolTypes.length];
        const symbolGenerator = d3.symbol().type(symbolDef.type).size(64);
        
        lineGroup.append('g')
          .attr('class', `symbols-${seriesIndex}`)
          .selectAll('path')
          .data(series.values)
          .join('path')
          .attr('transform', d => `translate(${x(d.year)},${y(d.count)})`)
          .attr('d', symbolGenerator)
          .attr('fill', symbolDef.filled ? color(series.name) : 'white')
          .attr('stroke', color(series.name))
          .attr('stroke-width', symbolDef.filled ? 0 : 2)
          .append('title')
          .text(d => `${series.name}\n${d.year}: ${d.count} patents`);
      });
      
      // Legend with interactive toggle
      const legend = svg.append('g')
        .attr('transform', `translate(${width + 10}, 0)`);
      
      seriesData.forEach((series, i) => {
        const g = legend.append('g')
          .attr('transform', `translate(0, ${i * 20})`)
          .attr('class', `legend-item-${i}`)
          .classed('legend-item-visible', true)
          .on('click', function() {
            // Toggle visibility
            const isVisible = visibleSeries.has(series.name);
            
            if (isVisible) {
              visibleSeries.delete(series.name);
            } else {
              visibleSeries.add(series.name);
            }
            
            // Update line classes
            svg.select(`.line-${i}`)
              .classed('chart-line-visible', !isVisible)
              .classed('chart-line-hidden', isVisible);
            
            svg.select(`.symbols-${i}`)
              .classed('chart-line-visible', !isVisible)
              .classed('chart-line-hidden', isVisible);
            
            // Update legend classes
            d3.select(this).select('text')
              .classed('legend-text-visible', !isVisible)
              .classed('legend-text-hidden', isVisible);
            
            d3.select(this).select('path, rect')
              .classed('legend-symbol-visible', !isVisible)
              .classed('legend-symbol-hidden', isVisible);
          });
        
        // Show symbol in legend
        const symbolDef = symbolTypes[i % symbolTypes.length];
        const symbolGenerator = d3.symbol().type(symbolDef.type).size(64);
        
        g.append('path')
          .attr('transform', 'translate(7.5, 7.5)')
          .attr('d', symbolGenerator)
          .attr('fill', symbolDef.filled ? color(series.name) : 'white')
          .attr('stroke', color(series.name))
          .attr('stroke-width', symbolDef.filled ? 0 : 2);
        
        g.append('text')
          .attr('x', 20)
          .attr('y', 12)
          .style('font-size', '11px')
          .text(series.name.length > 20 ? series.name.substring(0, 20) + '...' : series.name);
      });
      
      // Labels
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', height + 40)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Year');
      
      svg.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -height / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .text('Patent Count');
      
      // Title
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -20)
        .attr('text-anchor', 'middle')
        .style('font-size', '18px')
        .style('font-weight', 'bold')
        .text(title);
    }
  }, [activeTab, patentData, view, selectedApplicant, textSearch, trendsDateField, selectedStudy, trendsL4, trendsL7, filingTrendsGroupBy, trendsJurisdiction]);

  // Expected Expiration View - Chart 1: Granted vs Applications by Filing Year
  useEffect(() => {
    if (activeTab === 'trends' && view === 'expected-expiration' && patentData.length > 0) {
      const container = document.getElementById('expiry-chart-1');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Apply filters
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      
      // Filter by jurisdiction and classify
      filteredData = filteredData
        .filter(d => d.patentNo && d.filingDate)
        .map(d => {
          // Filter by L4
          if (selectedExpiryL4 !== 'ALL' && d.l4 !== selectedExpiryL4) return null;
          
          // Normalize patent number before classification
          const patentNo = (d.patentNo || '').replace(/\s+/g, '').replace(/[\-\/]/g, '').toUpperCase();
          const jurisdiction = patentNo.substring(0, 2);
          
          // Check jurisdiction filter
          if (trendsJurisdiction !== 'ALL' && jurisdiction !== trendsJurisdiction) return null;
          
          let isGranted = false;
          let isApplication = false;
          
          // All WO patents are applications
          if (jurisdiction === 'WO') {
            isApplication = true;
          } else {
            // Granted: ends with B (with or without number)
            if (patentNo.match(/B\d*$/)) {
              isGranted = true;
            }
            // Applications: ends with A followed by number (A1, A2, A3, A4, A9, etc.)
            else if (patentNo.match(/A\d+$/)) {
              isApplication = true;
            }
            // Special case: US ending with just A (no number) is granted
            else if (jurisdiction === 'US' && patentNo.endsWith('A')) {
              isGranted = true;
            }
          }
          
          return { ...d, isGranted, isApplication };
        })
        .filter(d => d !== null && (d.isGranted || d.isApplication));
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No data available</p>';
        return;
      }
      
      // Group by filing year
      const grantedByYear = d3.rollup(
        filteredData.filter(d => d.isGranted),
        v => v.length,
        d => parseInt(d.filingDate.substring(0, 4))
      );
      
      const appsByYear = d3.rollup(
        filteredData.filter(d => d.isApplication),
        v => v.length,
        d => parseInt(d.filingDate.substring(0, 4))
      );
      
      // Merge years
      const allYears = new Set([...grantedByYear.keys(), ...appsByYear.keys()]);
      const grantedData = Array.from(allYears).map(year => ({ 
        year, 
        count: grantedByYear.get(year) || 0 
      })).sort((a, b) => a.year - b.year);
      
      const appsData = Array.from(allYears).map(year => ({ 
        year, 
        count: appsByYear.get(year) || 0 
      })).sort((a, b) => a.year - b.year);
      
      // Chart dimensions
      const margin = { top: 20, right: 120, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const x = d3.scaleLinear()
        .domain([d3.min(grantedData, d => d.year), d3.max(grantedData, d => d.year)])
        .range([0, width]);
      
      const maxCount = d3.max([...grantedData, ...appsData], d => d.count);
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Lines
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      // Granted line
      svg.append('path')
        .datum(grantedData)
        .attr('fill', 'none')
        .attr('stroke', '#2171B5')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5')
        .attr('d', line);
      
      // Granted data points
      svg.selectAll('.granted-point')
        .data(grantedData)
        .join('circle')
        .attr('class', 'granted-point')
        .attr('cx', d => x(d.year))
        .attr('cy', d => y(d.count))
        .attr('r', 4)
        .attr('fill', '#2171B5');
      
      // Granted labels
      svg.selectAll('.granted-label')
        .data(grantedData)
        .join('text')
        .attr('class', 'granted-label')
        .attr('x', d => x(d.year))
        .attr('y', d => y(d.count) - 8)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', 'bold')
        .style('fill', '#2171B5')
        .text(d => d.count);
      
      // Applications line
      svg.append('path')
        .datum(appsData)
        .attr('fill', 'none')
        .attr('stroke', '#FD8D3C')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5')
        .attr('d', line);
      
      // Applications data points
      svg.selectAll('.apps-point')
        .data(appsData)
        .join('circle')
        .attr('class', 'apps-point')
        .attr('cx', d => x(d.year))
        .attr('cy', d => y(d.count))
        .attr('r', 4)
        .attr('fill', '#FD8D3C');
      
      // Applications labels
      svg.selectAll('.apps-label')
        .data(appsData)
        .join('text')
        .attr('class', 'apps-label')
        .attr('x', d => x(d.year))
        .attr('y', d => y(d.count) - 8)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', 'bold')
        .style('fill', '#FD8D3C')
        .text(d => d.count);
      
      // Calculate totals
      const grantedTotal = d3.sum(grantedData, d => d.count);
      const appsTotal = d3.sum(appsData, d => d.count);
      
      // Legend with totals
      const legend = svg.append('g')
        .attr('transform', `translate(${width + 10}, 0)`);
      
      legend.append('line')
        .attr('x1', 0).attr('x2', 20)
        .attr('y1', 10).attr('y2', 10)
        .attr('stroke', '#2171B5').attr('stroke-width', 2);
      legend.append('text')
        .attr('x', 25).attr('y', 15)
        .style('font-size', '12px')
        .text(`Granted (${grantedTotal})`);
      
      legend.append('line')
        .attr('x1', 0).attr('x2', 20)
        .attr('y1', 30).attr('y2', 30)
        .attr('stroke', '#FD8D3C').attr('stroke-width', 2);
      legend.append('text')
        .attr('x', 25).attr('y', 35)
        .style('font-size', '12px')
        .text(`Applications (${appsTotal})`);
      
      // Title
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -5)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text('Granted vs Applications by Filing Year');
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy, trendsJurisdiction, selectedExpiryL4]);

  // Expected Expiration View - Chart 2: Granted Patents by Expiry Year
  useEffect(() => {
    if (activeTab === 'trends' && view === 'expected-expiration' && patentData.length > 0) {
      const container = document.getElementById('expiry-chart-2');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Apply filters
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      
      // Filter by jurisdiction, classify, and calculate expiry
      filteredData = filteredData
        .filter(d => d.patentNo && d.filingDate)
        .map(d => {
          // Filter by L4
          if (selectedExpiryL4 !== 'ALL' && d.l4 !== selectedExpiryL4) return null;
          
          // Normalize patent number before classification
          const patentNo = (d.patentNo || '').replace(/\s+/g, '').replace(/[\-\/]/g, '').toUpperCase();
          const jurisdiction = patentNo.substring(0, 2);
          
          // Check jurisdiction filter
          if (trendsJurisdiction !== 'ALL' && jurisdiction !== trendsJurisdiction) return null;
          
          let isGranted = false;
          
          // WO patents are never granted (skip them)
          if (jurisdiction === 'WO') {
            return null;
          }
          
          // Granted: ends with B (with or without number)
          if (patentNo.match(/B\d*$/)) {
            isGranted = true;
          }
          // Special case: US ending with just A (no number) is granted
          else if (jurisdiction === 'US' && patentNo.endsWith('A')) {
            isGranted = true;
          }
          
          if (!isGranted) return null;
          
          const filingYear = parseInt(d.filingDate.substring(0, 4));
          const expiryYear = filingYear + 20;
          
          return { ...d, expiryYear };
        })
        .filter(d => d !== null);
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No granted patents found</p>';
        return;
      }
      
      // Group by expiry year
      const yearCounts = d3.rollup(
        filteredData,
        v => v.length,
        d => d.expiryYear
      );
      
      const data = Array.from(yearCounts, ([year, count]) => ({ year, count }))
        .sort((a, b) => a.year - b.year);
      
      // Chart dimensions with dynamic right margin
      const currentYear = new Date().getFullYear();
      const maxYear = d3.max(data, d => d.year);
      const rightMargin = (maxYear > currentYear + 15) ? 120 : 30;
      const margin = { top: 20, right: rightMargin, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const x = d3.scaleLinear()
        .domain(d3.extent(data, d => d.year))
        .range([0, width]);
      
      const maxCount = d3.max(data, d => d.count);
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Line
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      svg.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', '#2171B5')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5')
        .attr('d', line);
      
      // Data points
      svg.selectAll('.data-point')
        .data(data)
        .join('circle')
        .attr('class', 'data-point')
        .attr('cx', d => x(d.year))
        .attr('cy', d => y(d.count))
        .attr('r', 4)
        .attr('fill', '#2171B5');
      
      // Value labels
      svg.selectAll('.data-label')
        .data(data)
        .join('text')
        .attr('class', 'data-label')
        .attr('x', d => x(d.year))
        .attr('y', d => y(d.count) - 8)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', 'bold')
        .style('fill', '#2171B5')
        .text(d => d.count);
      
      // Calculate total
      const totalCount = d3.sum(data, d => d.count);
      
      // Current year line (dynamic)
      if (currentYear >= d3.min(data, d => d.year) && currentYear <= maxYear) {
        svg.append('line')
          .attr('x1', x(currentYear))
          .attr('x2', x(currentYear))
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', 'red')
          .attr('stroke-width', 3);
        
        // Calculate percentages
        const leftCount = d3.sum(data.filter(d => d.year < currentYear), d => d.count);
        const rightCount = d3.sum(data.filter(d => d.year >= currentYear), d => d.count);
        const leftPct = totalCount > 0 ? ((leftCount / totalCount) * 100).toFixed(1) : 0;
        const rightPct = totalCount > 0 ? ((rightCount / totalCount) * 100).toFixed(1) : 0;
        
        // Display percentages
        svg.append('text')
          .attr('x', x(currentYear) - 10)
          .attr('y', 20)
          .attr('text-anchor', 'end')
          .style('font-size', '12px')
          .style('font-weight', 'bold')
          .style('fill', 'red')
          .text(`Left: ${leftPct}%`);
        
        svg.append('text')
          .attr('x', x(currentYear) + 10)
          .attr('y', 20)
          .attr('text-anchor', 'start')
          .style('font-size', '12px')
          .style('font-weight', 'bold')
          .style('fill', 'green')
          .text(`Right: ${rightPct}%`);
      }
      
      // Title
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -5)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text('Granted Patents - Expected Expiry');
      
      // Total label in right margin
      svg.append('text')
        .attr('x', width + 10)
        .attr('y', 15)
        .attr('text-anchor', 'start')
        .style('font-size', '12px')
        .style('fill', '#2171B5')
        .text(`Granted (${totalCount})`);
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy, trendsJurisdiction, selectedExpiryL4]);

  // Expected Expiration View - Chart 3: Applications by Potential Expiry Year
  useEffect(() => {
    if (activeTab === 'trends' && view === 'expected-expiration' && patentData.length > 0) {
      const container = document.getElementById('expiry-chart-3');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      // Apply filters
      let filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      


      // Filter by jurisdiction, classify, and calculate potential expiry
      filteredData = filteredData
        .filter(d => d.patentNo && d.filingDate)
        .map(d => {
          // Filter by L4
          if (selectedExpiryL4 !== 'ALL' && d.l4 !== selectedExpiryL4) return null;
          
          // Normalize patent number before classification
          const patentNo = (d.patentNo || '').replace(/\s+/g, '').replace(/[\-\/]/g, '').toUpperCase();
          const jurisdiction = patentNo.substring(0, 2);
          
          // Check jurisdiction filter
          if (trendsJurisdiction !== 'ALL' && jurisdiction !== trendsJurisdiction) return null;
          
          let isApplication = false;
          
          // All WO patents are applications
          if (jurisdiction === 'WO') {
            isApplication = true;
          }
          // Applications: ends with A followed by number (A1, A2, A3, A4, A9, etc.)
          else if (patentNo.match(/A\d+$/)) {
            isApplication = true;
          }
          
          if (!isApplication) return null;
          
          const filingYear = parseInt(d.filingDate.substring(0, 4));
          const potentialExpiryYear = filingYear + 20;
          
          return { ...d, potentialExpiryYear };
        })
        .filter(d => d !== null);
      
      if (filteredData.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center py-8">No applications found</p>';
        return;
      }
      
      // Group by potential expiry year
      const yearCounts = d3.rollup(
        filteredData,
        v => v.length,
        d => d.potentialExpiryYear
      );
      
      const data = Array.from(yearCounts, ([year, count]) => ({ year, count }))
        .sort((a, b) => a.year - b.year);
      
      // Chart dimensions with dynamic right margin
      const currentYear = new Date().getFullYear();
      const maxYear = d3.max(data, d => d.year);
      const rightMargin = (maxYear > currentYear + 15) ? 120 : 30;
      const margin = { top: 20, right: rightMargin, bottom: 50, left: 60 };
      const width = container.clientWidth - margin.left - margin.right;
      const height = 300 - margin.top - margin.bottom;
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Scales
      const x = d3.scaleLinear()
        .domain(d3.extent(data, d => d.year))
        .range([0, width]);
      
      const maxCount = d3.max(data, d => d.count);
      const y = d3.scaleLinear()
        .domain([0, maxCount])
        .range([height, 0]);
      
      // Axes
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      const step = maxCount <= 10 ? 1 : maxCount <= 50 ? 5 : maxCount <= 100 ? 10 : 20;
      svg.append('g')
        .call(d3.axisLeft(y).tickValues(d3.range(0, maxCount + 1, step)).tickFormat(d3.format('d')))
        .style('font-size', '14px');
      
      // Line
      const line = d3.line()
        .x(d => x(d.year))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);
      
      svg.append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', '#FD8D3C')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '5,5')
        .attr('d', line);
      
      // Data points
      svg.selectAll('.data-point')
        .data(data)
        .join('circle')
        .attr('class', 'data-point')
        .attr('cx', d => x(d.year))
        .attr('cy', d => y(d.count))
        .attr('r', 4)
        .attr('fill', '#FD8D3C');
      
      // Value labels
      svg.selectAll('.data-label')
        .data(data)
        .join('text')
        .attr('class', 'data-label')
        .attr('x', d => x(d.year))
        .attr('y', d => y(d.count) - 8)
        .attr('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('font-weight', 'bold')
        .style('fill', '#FD8D3C')
        .text(d => d.count);
      
      // Calculate total
      const totalCount = d3.sum(data, d => d.count);
      
      // Current year line (dynamic)
      if (currentYear >= d3.min(data, d => d.year) && currentYear <= maxYear) {
        svg.append('line')
          .attr('x1', x(currentYear))
          .attr('x2', x(currentYear))
          .attr('y1', 0)
          .attr('y2', height)
          .attr('stroke', 'red')
          .attr('stroke-width', 3);
        
        // Calculate percentages
        const leftCount = d3.sum(data.filter(d => d.year < currentYear), d => d.count);
        const rightCount = d3.sum(data.filter(d => d.year >= currentYear), d => d.count);
        const leftPct = totalCount > 0 ? ((leftCount / totalCount) * 100).toFixed(1) : 0;
        const rightPct = totalCount > 0 ? ((rightCount / totalCount) * 100).toFixed(1) : 0;
        
        // Display percentages
        svg.append('text')
          .attr('x', x(currentYear) - 10)
          .attr('y', 20)
          .attr('text-anchor', 'end')
          .style('font-size', '12px')
          .style('font-weight', 'bold')
          .style('fill', 'red')
          .text(`Left: ${leftPct}%`);
        
        svg.append('text')
          .attr('x', x(currentYear) + 10)
          .attr('y', 20)
          .attr('text-anchor', 'start')
          .style('font-size', '12px')
          .style('font-weight', 'bold')
          .style('fill', 'green')
          .text(`Right: ${rightPct}%`);
      }
      
      // Title
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', -5)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text('Applications - Projected Expiry');
      
      // Total label in right margin
      svg.append('text')
        .attr('x', width + 10)
        .attr('y', 15)
        .attr('text-anchor', 'start')
        .style('font-size', '12px')
        .style('fill', '#FD8D3C')
        .text(`Applications (${totalCount})`);
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy, trendsJurisdiction, selectedExpiryL4]);

  // Valuation data processing
  useEffect(() => {
    if (activeTab === 'citations' && view === 'valuation' && patentData.length > 0) {
      const filteredData = applyFilters(patentData, {
          selectedApplicant,
          textSearch,
          searchableFields: selectedStudy?.searchableFields,
          selectedL2: null,
          patentNoSearch});
      const portfolioVal = calculatePortfolioValuation(filteredData, valuationMethod);
      setValuationData(portfolioVal);
    }
  }, [activeTab, view, patentData, selectedApplicant, textSearch, selectedStudy, valuationMethod]);

  // Valuation treemap visualization
  useEffect(() => {
    if (activeTab === 'citations' && view === 'valuation' && valuationData) {
      const container = document.getElementById('valuation-treemap');
      if (!container) return;
      
      d3.select(container).selectAll('*').remove();
      
      const l4Data = Object.entries(valuationData.l4Valuations)
        .map(([l4, data]) => ({
          name: l4,
          value: data.avgScore,
          count: data.count,
          avgScore: data.avgScore,
          patents: data.patents
        }))
        .sort((a, b) => b.avgScore - a.avgScore)
        .slice(0, 20);
      
      if (l4Data.length === 0) {
        container.innerHTML = '<p class="text-gray-600 text-center">No valuation data available</p>';
        return;
      }
      
      const width = container.clientWidth;
      const height = 600;
      
      const root = d3.hierarchy({ children: l4Data })
        .sum(d => d.value)
        .sort((a, b) => b.value - a.value);
      
      d3.treemap()
        .size([width, height])
        .padding(2)(root);
      
      const svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height);
      
      // Get monochrome palette and use discrete colors
      const colorScale = d3.scaleOrdinal(getStudyColors(selectedStudy, 'monochrome'));
      
      const cell = svg.selectAll('g')
        .data(root.leaves())
        .join('g')
        .attr('transform', d => `translate(${d.x0},${d.y0})`);
      
      cell.append('rect')
        .attr('width', d => d.x1 - d.x0)
        .attr('height', d => d.y1 - d.y0)
        .attr('fill', d => colorScale(d.data.name))
        .attr('stroke', 'white')
        .attr('stroke-width', 2);
      
      cell.append('text')
        .attr('x', 4)
        .attr('y', 20)
        .text(d => d.data.name)
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('fill', 'white')
        .style('text-shadow', '1px 1px 2px rgba(0,0,0,0.8)');
      
      cell.append('text')
        .attr('x', 4)
        .attr('y', 35)
        .text(d => `Avg: ${d.data.avgScore}`)
        .attr('font-size', '11px')
        .attr('fill', 'white')
        .style('text-shadow', '1px 1px 2px rgba(0,0,0,0.8)');
      
      cell.append('text')
        .attr('x', 4)
        .attr('y', 48)
        .text(d => `Patents: ${d.data.count}`)
        .attr('font-size', '10px')
        .attr('fill', 'white')
        .style('text-shadow', '1px 1px 2px rgba(0,0,0,0.8)');
      
      const tooltip = d3.select('body')
        .append('div')
        .style('position', 'absolute')
        .style('background', 'rgba(0, 0, 0, 0.9)')
        .style('color', 'white')
        .style('padding', '12px')
        .style('border-radius', '8px')
        .style('font-size', '13px')
        .style('pointer-events', 'none')
        .style('opacity', 0)
        .style('z-index', 1000)
        .style('white-space', 'pre-line');
      
      cell.on('mouseenter', function(event, d) {
        const topPatents = d.data.patents
          .sort((a, b) => b.valuation.score - a.valuation.score)
          .slice(0, 3)
          .map(p => `${p.patent.patentNo}: ${p.valuation.score}`)
          .join('\n');
        
        tooltip
          .style('opacity', 1)
          .html(`<strong>${d.data.name}</strong>\nAvg Score: ${d.data.avgScore}\nPatents: ${d.data.count}\n\nTop Patents:\n${topPatents}`)
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mousemove', function(event) {
        tooltip
          .style('left', (event.pageX + 10) + 'px')
          .style('top', (event.pageY - 10) + 'px');
      })
      .on('mouseleave', function() {
        tooltip.style('opacity', 0);
      });
    }
  }, [activeTab, view, valuationData, selectedStudy]);

  return (
    <div className="min-h-screen" style={{ background: '#0f172a' }}>
      {/* Header */}
      <div className="customer-header">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <svg viewBox="0 0 400 120" xmlns="http://www.w3.org/2000/svg" style={{ width: '280px', height: 'auto' }}>
            <defs>
              <linearGradient id="headerPrismGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style={{ stopColor: '#3B82F6', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#8B5CF6', stopOpacity: 1 }} />
              </linearGradient>
              <linearGradient id="headerPrismGrad2" x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#10B981', stopOpacity: 0.9 }} />
                <stop offset="100%" style={{ stopColor: '#3B82F6', stopOpacity: 0.9 }} />
              </linearGradient>
              <linearGradient id="headerTextGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#60A5FA', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: '#3B82F6', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#93C5FD', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <g transform="translate(20, 20)">
              <path d="M 30 0 L 40 30 L 30 50 L 20 30 Z" fill="url(#headerPrismGrad1)" opacity="0.9"/>
              <path d="M 0 30 L 20 30 L 15 50 L 5 40 Z" fill="#3B82F6" opacity="0.7"/>
              <path d="M 40 30 L 60 30 L 55 40 L 45 50 Z" fill="url(#headerPrismGrad2)" opacity="0.8"/>
              <path d="M 15 50 L 30 65 L 45 50 Z" fill="#8B5CF6" opacity="0.8"/>
            </g>
            <text x="95" y="55" fontFamily="Arial, sans-serif" fontSize="32" fill="url(#headerTextGradient)">
              <tspan fontWeight="700">Patents</tspan><tspan fontWeight="300">Prism</tspan>
            </text>
            <text x="95" y="75" fontFamily="Arial, sans-serif" fontSize="11" fontWeight="400" fill="#94A3B8" letterSpacing="1.5">
              PATENT ANALYSIS & INSIGHTS
            </text>
          </svg>
          <div className="form-radio-group">
            <span className="customer-email">{user.email}</span>
            <button
              onClick={onLogout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {selectedStudy && (
        <div className="tabs-container">
          <div className="tabs-card">
            <div className="tabs-wrapper">
              <div className="tabs-list">
                {Object.entries(VIEW_TABS_CONFIG).map(([key, config]) => {
                  const isEnabled = selectedStudy.enabledViews?.[key];
                  if (!isEnabled) return null;
                  
                  // Hide Citations tab if no citation data exists
                  if (key === 'citations') {
                    const hasCitationData = patentData.some(d => d.citedBy > 0 || d.citationsMade > 0 || d.familyCount > 0);
                    if (!hasCitationData) return null;
                  }
                  
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveTab(config.tabId)}
                      className={`py-3 px-2 font-semibold border-b-2 transition-colors ${
                        activeTab === config.tabId
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      {config.label}
                    </button>
                  );
                })}
              </div>
              {/* Right side controls - fixed position */}
              <div className="form-radio-group">
                {/* Study Selector */}
                {studies.length > 1 && (
                  <div className="study-name-row">
                    <label className="control-label">Study:</label>
                    <select
                      value={selectedStudy?.name || ''}
                      onChange={(e) => {
                        const study = studies.find(s => s.name === e.target.value);
                        loadStudyData(study);
                      }}
                      className="control-select"
                    >
                      {studies.map(study => (
                        <option key={study.name} value={study.name}>{study.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Definitions Button */}
                {selectedStudy?.definitionsFileUrl && (
                  <a
                    href={selectedStudy.definitionsFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:bg-gray-100 transition-colors rounded p-1"
                    title="Right-click to download file"
                  >
                    <i data-lucide="download" className="w-6 h-6 text-blue-600"></i>
                  </a>
                )}
                <button
                  onClick={() => setShowGuideModal(true)}
                  className="text-blue-600 hover:text-blue-700 text-xl font-bold border-2 border-blue-600 hover:border-blue-700 rounded-full w-7 h-7 flex items-center justify-center transition-colors"
                  title="Help"
                >
                  ?
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="content-container">
        {!selectedStudy && (
          <div className="no-data-message">No studies available</div>
        )}
        
        {selectedStudy && (
          <>
            {/* Control Bar / Ribbon - Hide for My Collection tab */}
            {activeTab !== 'advanced' && (
            <div className="tabs-card">
              <div className="control-ribbon-content">
                {/* Views Dropdown - Tab-specific */}
                <div className="study-name-row">
                  <label className="control-label">Views:</label>
                  <select 
                    value={view} 
                    onChange={(e) => setView(e.target.value)} 
                    className="control-select"
                  >
                    {activeTab === 'basic' && (
                      <>
                        <option value="applicant-list">Applicants</option>
                        <option value="collaborations">Collaborations</option>
                        <option value="technology-segments">R&D Preview</option>
                      </>
                    )}
                    {activeTab === 'innovation' && (
                      <>
                        <option value="rd-explorer">R&D Explorer</option>
                        <option value="whitespace">Whitespace</option>
                        <option value="deep-dives">Deep Dives</option>
                      </>
                    )}
                    {activeTab === 'trends' && (
                      <>
                        <option value="filing-trends">Filing Trends</option>
                        <option value="expected-expiration">Expected Expiration</option>
                        <option value="patent-lookup">Patent dates</option>
                      </>
                    )}
                    {activeTab === 'citations' && (
                      <>
                        <option value="impact-score">Impact Scores</option>
                        <option value="velocity-score">Velocity Scores</option>
                        <option value="dense-navigators">Dense Navigators</option>
                        <option value="strength-index">Strength Index</option>
                        <option value="valuation">Patent Valuation</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Date Field Selector - Trends tab only */}
                {activeTab === 'trends' && view === 'filing-trends' && (() => {
                  const dateFields = [];
                  if (patentData.some(d => d[trendsDateField])) dateFields.push({ value: 'filingDate', label: 'Filing Date' });
                  if (patentData.some(d => d.priorityDate)) dateFields.push({ value: 'priorityDate', label: 'Priority Date' });
                  if (patentData.some(d => d.publicationDate)) dateFields.push({ value: 'publicationDate', label: 'Publication Date' });
                  
                  return dateFields.length > 0 && (
                    <div className="study-name-row">
                      <label className="control-label">Date Field:</label>
                      <select
                        value={trendsDateField}
                        onChange={(e) => setTrendsDateField(e.target.value)}
                        className="control-select"
                      >
                        {dateFields.map(field => (
                          <option key={field.value} value={field.value}>{field.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* Jurisdiction Controls - Trends views (not Patent Lookup) */}
                {activeTab === 'trends' && (view === 'filing-trends' || view === 'expected-expiration') && (() => {
                  // Extract unique jurisdictions from patent numbers
                  const jurisdictions = Array.from(
                    new Set(
                      patentData
                        .map(d => d.patentNo ? d.patentNo.substring(0, 2).toUpperCase() : '')
                        .filter(j => j)
                    )
                  ).sort();

                  return (
                    <div className="study-name-row">
                      <label className="control-label">Jurisdiction:</label>
                      <select
                        value={trendsJurisdiction}
                        onChange={(e) => setTrendsJurisdiction(e.target.value)}
                        className="control-select"
                      >
                        <option value="ALL">All</option>
                        {jurisdictions.map(j => (
                          <option key={j} value={j}>{j}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* N Slider - Conditional */}
                {shouldShowNSlider() && (
                  <div className="study-name-row">
                    <label className="control-label">N: {nSlider}</label>
                    <input 
                      type="range" 
                      min="1" 
                      max={selectedAlphabet === 'ALL' ? 50 : 20} 
                      value={nSlider} 
                      onChange={(e) => setNSlider(parseInt(e.target.value))} 
                      className="w-20" 
                    />
                  </div>
                )}
{/* Text Search - Conditional */}
                {shouldShowTextSearch() && (
                  <div className="study-name-row">
                    <label className="control-label">Text:</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder='Search...'
                        value={textSearch}
                        onChange={(e) => setTextSearch(e.target.value)}
                        className="control-input wide"
                        title="Search syntax:
• &quot;quotes&quot; = exact phrase
• Space or + = AND (both terms)
• | = OR (either term)
• - = NOT (exclude term)
• * = wildcard (invert* matches inverter, inversion, etc.)

Plain terms match whole words only:
• driver finds &quot;driver&quot; but not &quot;driverless&quot;
• driver* finds both &quot;driver&quot; and &quot;driverless&quot;

Examples:
• battery lithium (both whole words)
• solar | wind (either word)
• AI -neural (AI but not neural)
• &quot;machine learn*&quot; (phrase with wildcard)"
                      />
                      {textSearch && (
                        <button
                          onClick={() => setTextSearch('')}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          title="Clear"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Applicant Search - Conditional */}
                {shouldShowApplicantSearch() && (
                  <div className="relative flex items-center gap-2">
                    <label className="control-label">Applicant:</label>
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search..."
                        value={applicantSearch}
                        onChange={(e) => setApplicantSearch(e.target.value)}
                        className="control-input"
                      />
                      {applicantSearch && (
                        <button
                          onClick={() => { setSelectedApplicant(''); setApplicantSearch(''); }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          title="Clear"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {applicantSearch && (
                      <div className="absolute z-10 top-full left-20 mt-1 w-64 bg-white border rounded shadow-lg max-h-48 overflow-y-auto">
                        <div 
                          onClick={() => { setSelectedApplicant(''); setApplicantSearch(''); }} 
                          className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        >
                          All Applicants
                        </div>
                        {applicants
                          .filter(app => app.toLowerCase().includes(applicantSearch.toLowerCase()))
                          .map(app => (
                            <div 
                              key={app} 
                              onClick={() => { setSelectedApplicant(app); setApplicantSearch(app); }} 
                              className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              {app}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Count Toggle - Hide for collaborations, Innovation tab, R&D view, and Citations tab */}
                {view !== 'collaborations' && activeTab !== 'innovation' && view !== 'technology-segments' && activeTab !== 'citations' && (
                  <div className="study-name-row">
                    <span className="control-label">Count:</span>
                    <button
                      onClick={() => setCountMode(countMode === 'original' ? 'individual' : 'original')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                        countMode === 'individual' ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        countMode === 'individual' ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                    <span className="text-sm whitespace-nowrap">{countMode === 'original' ? 'Original' : 'Individual'}</span>
                  </div>
                )}

                {/* Genus (L2) Filter - Innovation tab only - HIDDEN FOR NOW */}
                {false && activeTab === 'innovation' && (() => {
                  const l2Values = Array.from(new Set(patentData.map(d => d.l2).filter(l2 => l2))).sort();
                  return l2Values.length > 0 && (
                    <div className="study-name-row">
                      <label className="control-label">Genus:</label>
                      <select
                        value={selectedL2}
                        onChange={(e) => setSelectedL2(e.target.value)}
                        className="control-select"
                      >
                        <option value="">All</option>
                        {l2Values.map(l2 => (
                          <option key={l2} value={l2}>{l2}</option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {/* Patent Number Search - Innovation and Patent Lookup */}
                {(activeTab === 'innovation' || (activeTab === 'trends' && view === 'patent-lookup')) && (
                  <div className="study-name-row">
                    <label className="control-label">Patent #:</label>
                    <input
                      type="text"
                      placeholder="US12345678,EP98765..."
                      value={patentNoSearch}
                      onChange={(e) => setPatentNoSearch(e.target.value)}
                      className="p-2 border rounded text-sm w-64"
                      title="Enter patent numbers separated by commas (no spaces in patent numbers)"
                    />
                  </div>
                )}

                              </div>
            </div>
            )}

            {/* View Content */}
            <div className="content-card">
              {activeTab === 'basic' && view === 'applicant-list' && (
                <div>
                  {/* Description */}
                  <p className="view-description">Applicants in this portfolio. Click to see names.</p>
                  
                  {/* Alphabet Selector - Two Row Layout with ALL */}
                  <div className="alphabet-selector">
                    {/* Row 1: ALL + A-M */}
                    <div className="alphabet-row">
                      <button
                        onClick={() => setSelectedAlphabet('ALL')}
                        className={`w-10 h-8 rounded font-semibold text-sm ${
                          selectedAlphabet === 'ALL'
                            ? 'bg-blue-500 text-white'
                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                      >
                        ALL
                      </button>
                      {'ABCDEFGHIJKLM'.split('').map(letter => {
                        const hasApplicants = applicants.some(app => app.charAt(0).toUpperCase() === letter);
                        const applicantCount = applicants.filter(app => app.charAt(0).toUpperCase() === letter).length;
                        return (
                          <button
                            key={letter}
                            onClick={() => hasApplicants && setSelectedAlphabet(letter)}
                            disabled={!hasApplicants}
                            className={`w-10 h-8 rounded font-semibold text-sm relative ${
                              selectedAlphabet === letter
                                ? 'bg-blue-500 text-white'
                                : hasApplicants
                                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                            }`}
                          >
                            <span>{letter}</span>
                            {hasApplicants && (
                              <sub className="absolute bottom-0 right-1 text-xs">{applicantCount}</sub>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {/* Row 2: N-Z */}
                    <div className="flex gap-2">
                      {'NOPQRSTUVWXYZ'.split('').map(letter => {
                        const hasApplicants = applicants.some(app => app.charAt(0).toUpperCase() === letter);
                        const applicantCount = applicants.filter(app => app.charAt(0).toUpperCase() === letter).length;
                        return (
                          <button
                            key={letter}
                            onClick={() => hasApplicants && setSelectedAlphabet(letter)}
                            disabled={!hasApplicants}
                            className={`w-10 h-8 rounded font-semibold text-sm relative ${
                              selectedAlphabet === letter
                                ? 'bg-blue-500 text-white'
                                : hasApplicants
                                ? 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                            }`}
                          >
                            <span>{letter}</span>
                            {hasApplicants && (
                              <sub className="absolute bottom-0 right-1 text-xs">{applicantCount}</sub>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Treemap Description */}
                  <p className="text-sm text-gray-600 mb-3 mt-6">Top applicants by patent count. Use Slider and Count feature.</p>
                  
                  {/* Treemap */}
                  <div id="applicant-treemap" className="treemap-container"></div>
                </div>
              )}

              {activeTab === 'basic' && view === 'technology-segments' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Snapshot view of the R&D problem segments in your portfolio. Hover for details. Explore more in the Innovation & Trends tab.</p>
                  <div id="technology-treemap" className="treemap-container"></div>
                </div>
              )}

              {activeTab === 'basic' && view === 'collaborations' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Patent ownership breakdown. Solo patents and joint collaborations. Click to expand.</p>
                  <div className="view-tabs-list">
                    {Object.keys(collaborationData).length === 0 ? (
                      <p className="text-gray-600">Loading collaboration data...</p>
                    ) : (
                      Object.keys(collaborationData).sort((a, b) => parseInt(a) - parseInt(b)).map(size => {
                        const data = collaborationData[size];
                        const typeName = size === '1' ? 'Solo' : `${size}-way`;
                        const isExpanded = expandedCollabs[size];
                        
                        return (
                          <div key={size} className="border rounded">
                            <button
                              onClick={() => setExpandedCollabs(prev => ({ ...prev, [size]: !prev[size] }))}
                              className="w-full p-4 text-left flex justify-between items-center hover:bg-gray-50"
                            >
                              <span className="font-semibold text-lg">
                                {typeName} Collaborations: {data.total} patents
                              </span>
                              <span className="text-xl">{isExpanded ? '−' : '+'}</span>
                            </button>
                            
                            {isExpanded && (
                              <div className="p-4 bg-gray-50 border-t">
                                <div className="patent-results-table-wrapper">
                                  <table className="min-w-full bg-white border">
                                    <thead>
                                      <tr className="bg-gray-100">
                                        <th className="px-4 py-2 text-left border">Combination</th>
                                        <th className="px-4 py-2 text-left border">Patents</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(data.combinations)
                                        .sort((a, b) => b[1] - a[1])
                                        .map(([combo, count], idx) => (
                                          <tr key={idx} className="hover:bg-gray-50">
                                            <td className="px-4 py-2 border">{combo}</td>
                                            <td className="px-4 py-2 border">{count}</td>
                                          </tr>
                                        ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'innovation' && view === 'rd-explorer' && (
                <div>
                  {!selectedL4 ? (
                    <>
                      <p className="text-sm text-gray-600 mb-4">Click on R&D problem segments to understand solution approaches.</p>
                      <div id="l4-treemap" className="treemap-container"></div>
                      
                      {/* Patent Results Table - Show when any filter is active */}
                      {(textSearch || selectedApplicant || patentNoSearch) && (() => {
                        // Filter patents based on current filters
                        const filteredPatents = applyFilters(patentData, {
                            selectedApplicant,
                            textSearch,
                            searchableFields: selectedStudy?.searchableFields,
                            selectedL2,
          patentNoSearch}).filter(p => p.l4); // Only patents with L4
                        
                        // Group by L4
                        const patentsByL4 = {};
                        filteredPatents.forEach(patent => {
                          const l4 = patent.l4;
                          if (!patentsByL4[l4]) {
                            patentsByL4[l4] = [];
                          }
                          patentsByL4[l4].push(patent);
                        });
                        
                        const l4Categories = Object.keys(patentsByL4).sort();
                        
                        if (filteredPatents.length === 0) {
                          return null;
                        }
                        
                        // Determine title based on active filters
                        let title = 'Matching Patents';
                        if (textSearch && selectedApplicant) title = 'Text & Applicant Filter Results';
                        else if (textSearch && patentNoSearch) title = 'Text & Patent # Filter Results';
                        else if (selectedApplicant && patentNoSearch) title = 'Applicant & Patent # Filter Results';
                        else if (textSearch) title = 'Text Search Results';
                        else if (selectedApplicant) title = `Patents by ${selectedApplicant}`;
                        else if (patentNoSearch) title = 'Patent # Search Results';
                        
                        return (
                          <div className="mt-6 bg-white rounded shadow p-4">
                            <h3 className="text-lg font-semibold mb-4">{title} ({filteredPatents.length} results)</h3>
                            
                            {l4Categories.map(l4 => {
                              const patents = patentsByL4[l4];
                              const isExpanded = expandedL4Results.has(l4);
                              
                              return (
                                <div key={l4} className="border-b last:border-b-0">
                                  <div
                                    className="flex items-center justify-between py-3 px-2 cursor-pointer hover:bg-gray-50"
                                    onClick={() => {
                                      const newExpanded = new Set(expandedL4Results);
                                      if (isExpanded) {
                                        newExpanded.delete(l4);
                                      } else {
                                        newExpanded.add(l4);
                                      }
                                      setExpandedL4Results(newExpanded);
                                    }}
                                  >
                                    <span className="study-name">
                                      {isExpanded ? '▼' : '▶'} {l4} ({patents.length} patents)
                                    </span>
                                  </div>
                                  
                                  {isExpanded && (
                                    <div className="overflow-x-auto pb-3">
                                      <table className="patent-results-table-wrapper table">
                                        <thead className="patent-results-table-wrapper thead">
                                          <tr>
                                            <th className="patent-results-table-wrapper th">Flag</th>
                                            <th className="patent-results-table-wrapper th">Patent No</th>
                                            <th className="patent-results-table-wrapper th">Title</th>
                                            <th className="patent-results-table-wrapper th">Applicant</th>
                                            <th className="patent-results-table-wrapper th">Tech levers/Soln</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {patents.map((patent, idx) => {
                                            const isTagged = taggedPatents[patent.patentNo];
                                            return (
                                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                <td className="patent-results-table-wrapper td">
                                                  <button
                                                    onClick={() => toggleTag(patent.patentNo)}
                                                    className="text-2xl hover:scale-110 transition-transform"
                                                    title={isTagged ? 'Remove flag' : 'Flag patent'}
                                                  >
                                                    {isTagged ? '⭐' : '☆'}
                                                  </button>
                                                </td>
                                                <td className="patent-results-table-wrapper td">{patent.patentNo || ''}</td>
                                                <td className="patent-results-table-wrapper td">{toSentenceCase(patent.title || '')}</td>
                                                <td className="patent-results-table-wrapper td">{patent.applicant || ''}</td>
                                                <td className="patent-results-table-wrapper td">{patent.l7 || ''}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-4 mb-4">
                        <button
                          onClick={() => setSelectedL4(null)}
                          className="btn-modal-save"
                        >
                          ← Back to R&D Focus
                        </button>
                        <h2 className="text-xl font-bold">Solution approaches/Technology levers for {selectedL4} problems</h2>
                      </div>
                      <div id="l9-treemap" className="treemap-container"></div>
                      
                      {/* Filtered Patents Table in L7 view */}
                      {(textSearch || selectedApplicant || patentNoSearch) && (() => {
                        const filteredPatents = applyFilters(patentData, {
                            selectedApplicant,
                            textSearch,
                            searchableFields: selectedStudy?.searchableFields,
                            selectedL2,
                            patentNoSearch
                        }).filter(p => p.l4 === selectedL4);
                        
                        if (filteredPatents.length === 0) return null;
                        
                        let title = 'Matching Patents';
                        if (textSearch && selectedApplicant) title = 'Text & Applicant Filter Results';
                        else if (textSearch) title = 'Text Search Results';
                        else if (selectedApplicant) title = `Patents by ${selectedApplicant}`;
                        else if (patentNoSearch) title = 'Patent # Search Results';
                        
                        return (
                          <div className="mt-6 bg-white rounded shadow p-4">
                            <h3 className="text-lg font-semibold mb-4">{title} in {selectedL4} ({filteredPatents.length} results)</h3>
                            <div className="patent-results-table-wrapper">
                              <table className="min-w-full">
                                <thead className="patent-results-table-wrapper thead">
                                  <tr>
                                    <th className="patent-results-table-wrapper th">Flag</th>
                                    <th className="patent-results-table-wrapper th">Patent No</th>
                                    <th className="patent-results-table-wrapper th">Title</th>
                                    <th className="patent-results-table-wrapper th">Applicant</th>
                                    <th className="patent-results-table-wrapper th">Tech levers/Soln</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredPatents.map((patent, idx) => {
                                    const isTagged = taggedPatents[patent.patentNo];
                                    return (
                                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                        <td className="patent-results-table-wrapper td">
                                          <button
                                            onClick={() => toggleTag(patent.patentNo)}
                                            className="text-2xl hover:scale-110 transition-transform"
                                            title={isTagged ? 'Remove flag' : 'Flag patent'}
                                          >
                                            {isTagged ? '⭐' : '☆'}
                                          </button>
                                        </td>
                                        <td className="patent-results-table-wrapper td">{patent.patentNo || ''}</td>
                                        <td className="patent-results-table-wrapper td">{toSentenceCase(patent.title || '')}</td>
                                        <td className="patent-results-table-wrapper td">{patent.applicant || ''}</td>
                                        <td className="patent-results-table-wrapper td">{patent.l7 || ''}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
              {activeTab === 'innovation' && view === 'whitespace' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    R&D segments vs Tech Levers | Thicker blue borders indicate higher forward citation strength. Click cells to view patents.
                  </p>
                  <div id="whitespace-heatmap" style={{ width: '100%', overflowX: 'auto' }}></div>
                  
                  {/* Patents table for selected cell */}
                  {whitespaceSelection && (() => {
                    const { l4, l7 } = whitespaceSelection;
                    const patents = applyFilters(patentData, {
                      selectedApplicant,
                      textSearch,
                      searchableFields: selectedStudy?.searchableFields,
                      selectedL2,
          patentNoSearch}).filter(p => p.l4 === l4 && p.l7 === l7);
                    
                    if (patents.length === 0) return null;
                    
                    return (
                      <div className="mt-6 bg-white rounded shadow p-4">
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="font-semibold">
                            {l4} → {l7} ({patents.length} patent{patents.length !== 1 ? 's' : ''})
                          </h4>
                          <button
                            onClick={() => setWhitespaceSelection(null)}
                            className="text-sm text-gray-600 hover:text-gray-800"
                          >
                            ✕ Close
                          </button>
                        </div>
                        <div className="patent-results-table-wrapper">
                          <table className="min-w-full">
                            <thead className="patent-results-table-wrapper thead">
                              <tr>
                                <th className="patent-results-table-wrapper th">Flag</th>
                                <th className="patent-results-table-wrapper th">Patent No</th>
                                <th className="patent-results-table-wrapper th">Title</th>
                                <th className="patent-results-table-wrapper th">Applicant</th>
                              </tr>
                            </thead>
                            <tbody>
                              {patents.map((patent, idx) => {
                                const isTagged = taggedPatents[patent.patentNo];
                                return (
                                  <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="patent-results-table-wrapper td">
                                      <button
                                        onClick={() => toggleTag(patent.patentNo)}
                                        className="text-2xl hover:scale-110 transition-transform"
                                        title={isTagged ? 'Remove flag' : 'Flag patent'}
                                      >
                                        {isTagged ? '⭐' : '☆'}
                                      </button>
                                    </td>
                                    <td className="patent-results-table-wrapper td">{patent.patentNo}</td>
                                    <td className="patent-results-table-wrapper td">{toSentenceCase(patent.title || '')}</td>
                                    <td className="patent-results-table-wrapper td">{patent.applicant}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {activeTab === 'innovation' && view === 'deep-dives' && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Deep dive into the relations between R&D segments & Solution approaches. Click on the patent count number to expand and see details.</p>
                  
                  {/* Patent # Search Results - bypass L4 selector */}
                  {patentNoSearch && (() => {
                    const matchingPatents = applyFilters(patentData, {
                      selectedApplicant,
                      textSearch,
                      searchableFields: selectedStudy?.searchableFields,
                      selectedL2,
                      patentNoSearch
                    }).filter(p => p.l4);
                    
                    if (matchingPatents.length === 0) return null;
                    
                    return (
                      <div className="bg-white rounded shadow p-4 mb-6">
                        <h4 className="font-semibold mb-4">Patent Search Results ({matchingPatents.length})</h4>
                        <div className="patent-results-table-wrapper">
                          <table className="min-w-full border">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-2 py-2 text-left text-sm font-semibold border">Flag</th>
                                <th className="px-2 py-2 text-left text-sm font-semibold border">Patent No.</th>
                                <th className="px-2 py-2 text-left text-sm font-semibold border">Title</th>
                                <th className="px-2 py-2 text-left text-sm font-semibold border">Applicant</th>
                                <th className="px-2 py-2 text-left text-sm font-semibold border">R&D Area (L4)</th>
                                <th className="px-2 py-2 text-left text-sm font-semibold border">Tech Levers (L7)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {matchingPatents.map((patent, idx) => {
                                const isTagged = taggedPatents[patent.patentNo];
                                return (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-2 py-2 text-sm border">
                                      <button
                                        onClick={() => toggleTag(patent.patentNo)}
                                        className="text-2xl hover:scale-110 transition-transform"
                                        title={isTagged ? 'Remove flag' : 'Flag patent'}
                                      >
                                        {isTagged ? '⭐' : '☆'}
                                      </button>
                                    </td>
                                    <td className="px-2 py-2 text-sm border">{patent.patentNo}</td>
                                    <td className="px-2 py-2 text-sm border">{toSentenceCase(patent.title || '')}</td>
                                    <td className="px-2 py-2 text-sm border">{patent.applicant}</td>
                                    <td className="px-2 py-2 text-sm border">{patent.l4}</td>
                                    <td className="px-2 py-2 text-sm border">{patent.l7}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* L4 Selector */}
                  <div className="mb-6">
                    <label className="text-sm font-semibold mr-3">Select Technology Area:</label>
                    <select
                      value={deepDivesL4}
                      onChange={(e) => setDeepDivesL4(e.target.value)}
                      className="p-2 border rounded"
                    >
                      <option value="">-- Select --</option>
                      {Array.from(new Set(patentData.map(d => d.l4).filter(l4 => l4))).sort().map(l4 => (
                        <option key={l4} value={l4}>{l4}</option>
                      ))}
                    </select>
                  </div>

                  {/* Combinations Matrix Table */}
                  {deepDivesL4 && (
                    <div className="deep-dives-matrix">
                      {deepDivesCombos.length === 0 ? (
                        <p className="p-4 text-gray-600">No combinations found for this R&D Focus</p>
                      ) : (
                        (() => {
                          // Extract all unique L7 levers and calculate totals
                          const leverTotals = new Map();
                          deepDivesCombos.forEach(item => {
                            const levers = item.combination.split(',').map(l => l.trim());
                            levers.forEach(lever => {
                              leverTotals.set(lever, (leverTotals.get(lever) || 0) + item.count);
                            });
                          });
                          
                          const allLevers = Array.from(leverTotals.keys()).sort();
                          
                          return (
                            <div className="patent-results-table-wrapper">
                              <table className="min-w-full border-collapse">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="px-6 py-3 text-center text-sm font-semibold bg-blue-50 border-r border-gray-300">
                                      <div>Patent</div>
                                      <div>Count</div>
                                    </th>
                                    {allLevers.map(lever => (
                                      <th key={lever} className="px-4 py-3 text-center text-sm font-semibold border-r border-gray-300">
                                        <div>{lever}</div>
                                        <div className="form-help-text">({leverTotals.get(lever)})</div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {deepDivesCombos.map((item, idx) => {
                                    const leversInCombo = item.combination.split(',').map(l => l.trim());
                                    const isExpanded = expandedRows.has(idx);
                                    const toggleRow = () => {
                                      const newExpanded = new Set(expandedRows);
                                      if (isExpanded) {
                                        newExpanded.delete(idx);
                                      } else {
                                        newExpanded.add(idx);
                                      }
                                      setExpandedRows(newExpanded);
                                    };
                                    
                                    return (
                                      <React.Fragment key={idx}>
                                        <tr className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                          <td 
                                            className="px-6 py-3 text-center font-bold bg-blue-50 cursor-pointer hover:bg-blue-100 transition-colors border-r border-gray-300"
                                            onClick={toggleRow}
                                            title="Click to view patent details"
                                          >
                                            <span className="underline">{item.count}</span>
                                            <span className="ml-2 text-xs">{isExpanded ? '▼' : '▶'}</span>
                                          </td>
                                          {allLevers.map(lever => (
                                            <td key={lever} className="px-4 py-3 text-center border-r border-gray-200">
                                              {leversInCombo.includes(lever) ? (
                                                <span className="text-green-600 text-xl font-bold">✓</span>
                                              ) : (
                                                <span className="text-gray-300">—</span>
                                              )}
                                            </td>
                                          ))}
                                        </tr>
                                        {isExpanded && (
                                          <tr className="bg-gray-50 border-t-2 border-blue-300">
                                            <td colSpan={allLevers.length + 1} className="px-6 py-4">
                                              <div className="bg-white rounded shadow-sm p-4">
                                                <h4 className="font-semibold mb-1 text-lg">Technology Area: {deepDivesL4}</h4>
                                                <h4 className="font-semibold mb-3 text-lg">Patents with combination: {item.combination}</h4>
                                                {(() => {
                                                  // Apply centralized filters
                                                  let matchingPatents = applyFilters(patentData.filter(p => p.l4 === deepDivesL4 && p.l7 === item.combination), {
                                                      selectedApplicant,
                                                      textSearch,
                                                      searchableFields: selectedStudy?.searchableFields,
                                                      selectedL2: null,
          patentNoSearch});
                                                  
                                                  const hasL11 = matchingPatents.some(p => p.l11);
                                                  
                                                  return (
                                                    <div className="patent-results-table-wrapper">
                                                      <table className="min-w-full border">
                                                        <thead className="bg-gray-100">
                                                          <tr>
                                                            <th className="px-2 py-2 text-left text-sm font-semibold border">Flag</th>
                                                            <th className="px-2 py-2 text-left text-sm font-semibold border">Patent no.</th>
                                                            <th className="px-2 py-2 text-left text-sm font-semibold border">Tech summary</th>
                                                            {hasL11 && <th className="px-2 py-2 text-left text-sm font-semibold border">Innovation</th>}
                                                          </tr>
                                                        </thead>
                                                        <tbody>
                                                          {matchingPatents.map((patent, pIdx) => {
                                                            const isTagged = taggedPatents[patent.patentNo];
                                                            return (
                                                              <tr key={pIdx} className="hover:bg-gray-50">
                                                                <td className="px-2 py-2 text-sm border">
                                                                  <button
                                                                    onClick={() => toggleTag(patent.patentNo)}
                                                                    className="text-2xl hover:scale-110 transition-transform"
                                                                    title={isTagged ? 'Remove flag' : 'Flag patent'}
                                                                  >
                                                                    {isTagged ? '⭐' : '☆'}
                                                                  </button>
                                                                </td>
                                                                <td className="px-2 py-2 text-sm border">{patent['patent no.'] || patent.patentNo || 'N/A'}</td>
                                                                <td className="px-2 py-2 text-sm border">{patent.l9 || 'N/A'}</td>
                                                                {hasL11 && <td className="px-2 py-2 text-sm border">{patent.l11 || 'N/A'}</td>}
                                                              </tr>
                                                            );
                                                          })}
                                                        </tbody>
                                                      </table>
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'innovation' && view !== 'rd-explorer' && view !== 'whitespace' && view !== 'deep-dives' && <div>Innovation (other views to be implemented)</div>}
              
              {/* TRENDS TAB */}
              {activeTab === 'trends' && (
                <div>
                  {/* Filing Trends View - Consolidated */}
                  {view === 'filing-trends' && (
                    <>
                      {/* Group by selector */}
                      <div className="mb-6">
                        <label className="text-sm font-semibold mr-3">Group by:</label>
                        <select
                          value={filingTrendsGroupBy === 'overall' ? 'overall' : trendsL4}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === 'overall') {
                              setFilingTrendsGroupBy('overall');
                              setTrendsL4('');
                              setTrendsL7('');
                            } else {
                              setFilingTrendsGroupBy('rd');
                              setTrendsL4(value);
                              setTrendsL7('');
                            }
                          }}
                          className="p-2 border rounded w-96"
                        >
                          <option value="overall">Overall</option>
                          {Array.from(new Set(patentData.map(d => d.l4).filter(l4 => l4))).sort().map(l4 => (
                            <option key={l4} value={l4}>{l4}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Charts - 4x1 Layout */}
                      <div className="space-y-6">
                        <div className="bg-white rounded shadow p-4">
                          <div id="trends-chart-1"></div>
                        </div>
                        
                        <div className="bg-white rounded shadow p-4">
                          <div id="trends-chart-2"></div>
                        </div>
                        
                        <div className="bg-white rounded shadow p-4">
                          <div id="trends-chart-3"></div>
                        </div>
                        
                        <div className="bg-white rounded shadow p-4">
                          <div id="trends-chart-4"></div>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Expected Expiration View */}
                  {view === 'expected-expiration' && (
                    <>
                      {/* L4 Filter */}
                      <div className="form-group">
                        <label className="text-sm font-medium text-gray-700 mr-2">Group by:</label>
                        <select
                          value={selectedExpiryL4}
                          onChange={(e) => setSelectedExpiryL4(e.target.value)}
                          className="p-2 border rounded w-96"
                        >
                          <option value="ALL">Overall</option>
                          {Array.from(new Set(patentData.filter(d => d.l4).map(d => d.l4))).sort().map(l4 => (
                            <option key={l4} value={l4}>{l4}</option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="bg-white rounded shadow p-4">
                          <div id="expiry-chart-1"></div>
                        </div>
                        
                        <div className="bg-white rounded shadow p-4">
                          <div id="expiry-chart-2"></div>
                        </div>
                        
                        <div className="bg-white rounded shadow p-4">
                          <div id="expiry-chart-3"></div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Patent Lookup View */}
                  {view === 'patent-lookup' && (
                    <>
                      {(() => {
                        // Check if any filters are active
                        const hasFilters = selectedApplicant || textSearch || patentNoSearch;

                        // Calculate expiry helper
                        const calculateExpiry = (filingDate) => {
                          if (!filingDate) return 'N/A';
                          const year = parseInt(filingDate.substring(0, 4));
                          if (isNaN(year)) return 'N/A';
                          return year + 20;
                        };

                        // Show empty table if no filters
                        if (!hasFilters) {
                          return (
                            <div className="bg-white rounded shadow p-4">
                              <h3 className="text-lg font-semibold mb-4">Patent Details</h3>
                              <div className="patent-results-table-wrapper">
                                <table className="patent-results-table-wrapper table">
                                  <thead className="patent-results-table-wrapper thead">
                                    <tr>
                                      <th className="patent-results-table-wrapper th">Patent No</th>
                                      <th className="patent-results-table-wrapper th">Title</th>
                                      <th className="patent-results-table-wrapper th">Applicant</th>
                                      <th className="patent-results-table-wrapper th">R&D</th>
                                      <th className="px-3 py-2 text-left font-semibold border whitespace-nowrap">Filing Date</th>
                                      <th className="px-3 py-2 text-left font-semibold border whitespace-nowrap">Expected Expiry</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td colSpan="6" className="px-3 py-8 text-center text-gray-500">
                                        Use filters above to search for patents
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        }

                        // Apply filters
                        let filteredData = applyFilters(patentData, {
                            selectedApplicant,
                            textSearch,
                            searchableFields: selectedStudy?.searchableFields,
                            selectedL2: null,
          patentNoSearch});

                        // Apply patent number filter if provided
                        if (patentNoSearch) {
                          const searchTerms = patentNoSearch.split(',').map(t => t.trim()).filter(t => t);
                          if (searchTerms.length > 0) {
                            filteredData = filteredData.filter(patent => {
                              const normalized = normalizePatentNo(patent.patentNo || '');
                              return searchTerms.some(term => normalized.includes(normalizePatentNo(term)));
                            });
                          }
                        }

                        if (filteredData.length === 0) {
                          return (
                            <div className="bg-yellow-50 border border-yellow-200 rounded p-6 text-center">
                              <p className="text-gray-700">No patents found matching the current filters.</p>
                              <p className="text-sm text-gray-600 mt-2">Try adjusting your search criteria.</p>
                            </div>
                          );
                        }

                        return (
                          <div className="bg-white rounded shadow p-4">
                            <h3 className="text-lg font-semibold mb-4">Patent Details ({filteredData.length} patents)</h3>
                            <div className="patent-results-table-wrapper">
                              <table className="patent-results-table-wrapper table">
                                <thead className="patent-results-table-wrapper thead">
                                  <tr>
                                    <th className="patent-results-table-wrapper th">Patent No</th>
                                    <th className="patent-results-table-wrapper th">Title</th>
                                    <th className="patent-results-table-wrapper th">Applicant</th>
                                    <th className="patent-results-table-wrapper th">R&D</th>
                                    <th className="px-3 py-2 text-left font-semibold border whitespace-nowrap">Filing Date</th>
                                    <th className="px-3 py-2 text-left font-semibold border whitespace-nowrap">Expected Expiry</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredData.map((patent, idx) => (
                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="patent-results-table-wrapper td">{patent.patentNo || ''}</td>
                                      <td className="patent-results-table-wrapper td">{toSentenceCase(patent.title || '')}</td>
                                      <td className="patent-results-table-wrapper td">{patent.applicant || ''}</td>
                                      <td className="patent-results-table-wrapper td">{patent.l4 || ''}</td>
                                      <td className="px-3 py-2 border whitespace-nowrap">{patent.filingDate || ''}</td>
                                      <td className="px-3 py-2 border whitespace-nowrap">{calculateExpiry(patent.filingDate)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}
              
              {/* CITATIONS TAB */}
              {activeTab === 'citations' && (
                <div>
                  {/* Impact Score View */}
                  {view === 'impact-score' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Impact Scores</h3>
                    <p id="impact-description" className="text-sm text-gray-600 mb-6">Measures influence - Patents with no citations are excluded. Scores above threshold X.XX are more impactful</p>
                    
                    {/* L4 Treemap */}
                    <div className="bg-white rounded shadow p-4 mb-6">
                      <h4 className="font-medium mb-3">Impact by R&D Area</h4>
                      <div id="impact-l4-treemap" style={{ minHeight: '400px' }}></div>
                    </div>
                    
                    {/* High-Impact Patents Table */}
                    <div className="bg-white rounded shadow p-4">
                      <h4 className="font-medium mb-3">Top High-Impact Patents (Above Threshold)</h4>
                      <div className="patent-results-table-wrapper">
                        <table className="min-w-full">
                          <thead className="patent-results-table-wrapper thead">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Patent No.</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Title</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Applicant</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">R&D Area</th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">Impact Score</th>
                            </tr>
                          </thead>
                          <tbody id="impact-table-body">
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  )}
                  
                  {/* Velocity Scores View */}
                  {view === 'velocity-score' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Velocity Scores</h3>
                    <p id="velocity-description" className="text-sm text-gray-600 mb-4">Measures momentum - Patents with no citations are excluded. Scores above threshold X.XX show rapid citation growth</p>
                    
                    {/* Year Filter Slider */}
                    <div className="bg-gray-50 p-4 rounded mb-6">
                      <label className="form-label">
                        Recent Patents (Last {velocityYears} {velocityYears === 1 ? 'year' : 'years'})
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={velocityYears}
                        onChange={(e) => setVelocityYears(parseInt(e.target.value))}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-gray-600 mt-1">
                        <span>1 year</span>
                        <span>2 years</span>
                        <span>3 years</span>
                        <span>4 years</span>
                        <span>5 years</span>
                      </div>
                    </div>
                    
                    {/* L4 Treemap */}
                    <div className="bg-white rounded shadow p-4 mb-6">
                      <h4 className="font-medium mb-3">Velocity by R&D Area</h4>
                      <div id="velocity-l4-treemap" style={{ minHeight: '400px' }}></div>
                    </div>
                    
                    {/* High-Velocity Patents Table */}
                    <div className="bg-white rounded shadow p-4">
                      <h4 className="font-medium mb-3">Top High-Velocity Patents (Above Threshold)</h4>
                      <div className="patent-results-table-wrapper">
                        <table className="min-w-full">
                          <thead className="patent-results-table-wrapper thead">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Patent No.</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Title</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Applicant</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">R&D Area</th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">Velocity Score</th>
                            </tr>
                          </thead>
                          <tbody id="velocity-table-body">
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  )}
                  
                  {/* Patent Strength Index View */}
                  {view === 'strength-index' && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-4">Strength Index</h3>
                    <p id="strength-description" className="text-sm text-gray-600 mb-6">Measures value - Strength scores above threshold X.XX indicate higher value (Influential + well-protected + Original)</p>
                    
                    {/* L4 Treemap */}
                    <div className="bg-white rounded shadow p-4 mb-6">
                      <h4 className="font-medium mb-3">Strength by R&D Area</h4>
                      <div id="strength-l4-treemap" style={{ minHeight: '400px' }}></div>
                    </div>
                    
                    {/* High-Strength Patents Table */}
                    <div className="bg-white rounded shadow p-4">
                      <h4 className="font-medium mb-3">Top High-Strength Patents (Above Threshold)</h4>
                      <div className="patent-results-table-wrapper">
                        <table className="min-w-full">
                          <thead className="patent-results-table-wrapper thead">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Patent No.</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Title</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Applicant</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">R&D Area</th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">Strength Score</th>
                            </tr>
                          </thead>
                          <tbody id="strength-table-body">
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  )}
                  
                  {/* Dense Navigators View */}
                  {view === 'dense-navigators' && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Dense Navigators</h3>
                    <p id="navigator-description" className="text-sm text-gray-600 mb-6">Patents with high backward citations build on extensive prior art. Scores above threshold X.XX are significant</p>
                    
                    {/* L4 Treemap */}
                    <div className="bg-white rounded shadow p-4 mb-6">
                      <h4 className="font-medium mb-3">Backward Citations by R&D Area</h4>
                      <div id="navigator-l4-treemap" style={{ minHeight: '400px' }}></div>
                    </div>
                    
                    {/* High Navigator Patents Table */}
                    <div className="bg-white rounded shadow p-4">
                      <h4 className="font-medium mb-3">Top Dense Navigator Patents (Above Threshold)</h4>
                      <div className="patent-results-table-wrapper">
                        <table className="min-w-full">
                          <thead className="patent-results-table-wrapper thead">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Patent No.</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Title</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">Applicant</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold">R&D Area</th>
                              <th className="px-4 py-2 text-right text-sm font-semibold">Backward Citations</th>
                            </tr>
                          </thead>
                          <tbody id="navigator-table-body">
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  )}
                  {/* VALUATION VIEW */}
                  {view === 'valuation' && (
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Patent Valuation Analysis</h3>
                      <p className="text-sm text-gray-600 mb-6">
                        Comprehensive patent value scoring based on citation velocity, family size, remaining life and grant status.
                      </p>

                      {/* Methodology Selector */}
                      <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-6">
                        <div className="flex items-center gap-4">
                          <label className="text-sm font-semibold text-blue-900">Valuation Method:</label>
                          <select
                            value={valuationMethod}
                            onChange={(e) => setValuationMethod(e.target.value)}
                            className="p-2 border rounded text-sm font-medium"
                          >
                            <option value="standard">Standard</option>
                            <option value="citationFocused">Citation Focused</option>
                            <option value="geoCoverage">Family Size</option>
                            <option value="lifetimeValue">Lifetime Value</option>
                          </select>
                          <span className="text-sm text-gray-600 italic">
                            {VALUATION_METHODS[valuationMethod]?.description}
                          </span>
                        </div>
                      </div>

                      {/* Portfolio Summary Cards */}
                      {valuationData && (
                        <>
                          <div className="grid grid-cols-6 gap-3 mb-4">
                            <div className="bg-white rounded shadow p-3">
                              <div className="text-xs text-gray-600 mb-1">Total Patents</div>
                              <div className="text-xl font-bold text-blue-600">
                                {valuationData.portfolioMetrics.totalPatents}
                              </div>
                            </div>

                            <div className="bg-white rounded shadow p-3">
                              <div className="text-xs text-gray-600 mb-1">Avg Score</div>
                              <div className="text-xl font-bold text-blue-600">
                                {valuationData.portfolioMetrics.avgScore}
                              </div>
                            </div>

                            <div className="bg-white rounded shadow p-3">
                              <div className="text-xs text-gray-600 mb-1">Very High (Top 25%)</div>
                              <div className="text-xl font-bold text-green-600">
                                {valuationData.portfolioMetrics.tierCounts['Very High']}
                              </div>
                            </div>

                            <div className="bg-white rounded shadow p-3">
                              <div className="text-xs text-gray-600 mb-1">High (50-75%)</div>
                              <div className="text-xl font-bold text-teal-600">
                                {valuationData.portfolioMetrics.tierCounts['High']}
                              </div>
                            </div>

                            <div className="bg-white rounded shadow p-3">
                              <div className="text-xs text-gray-600 mb-1">Medium (25-50%)</div>
                              <div className="text-xl font-bold text-yellow-600">
                                {valuationData.portfolioMetrics.tierCounts['Medium']}
                              </div>
                            </div>

                            <div className="bg-white rounded shadow p-3">
                              <div className="text-xs text-gray-600 mb-1">Low (Bottom 25%)</div>
                              <div className="text-xl font-bold text-gray-600">
                                {valuationData.portfolioMetrics.tierCounts['Low']}
                              </div>
                            </div>
                          </div>
                          
                          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                            <div className="text-sm font-semibold mb-2">Score Thresholds:</div>
                            <div className="text-xs text-gray-700 grid grid-cols-4 gap-2">
                              <div>Very High: ≥{valuationData.portfolioMetrics.percentiles.p75}</div>
                              <div>High: {valuationData.portfolioMetrics.percentiles.p50}-{valuationData.portfolioMetrics.percentiles.p75}</div>
                              <div>Medium: {valuationData.portfolioMetrics.percentiles.p25}-{valuationData.portfolioMetrics.percentiles.p50}</div>
                              <div>Low: &lt;{valuationData.portfolioMetrics.percentiles.p25}</div>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Value Treemap by Technology Area */}
                      <div className="bg-white rounded shadow p-4 mb-6">
                        <h4 className="font-medium mb-3">Average of Patent Values by R&D Area</h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Darker colors indicate higher average valuation scores. Hover for details.
                        </p>
                        <div id="valuation-treemap" style={{ minHeight: '600px' }}></div>
                      </div>

                      {/* Controls */}
                      <div className="bg-gray-50 p-4 rounded mb-4 flex gap-4 items-center">
                        <label className="text-sm font-medium">Minimum Score:</label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="5"
                          value={valuationMinScore}
                          onChange={(e) => setValuationMinScore(parseInt(e.target.value))}
                          className="w-40"
                        />
                        <span className="text-sm font-semibold">{valuationMinScore}</span>

                        <label className="text-sm font-medium ml-6">Sort By:</label>
                        <select
                          value={valuationSortBy}
                          onChange={(e) => setValuationSortBy(e.target.value)}
                          className="p-2 border rounded text-sm"
                        >
                          <option value="score">Valuation Score</option>
                          <option value="citations">Citations</option>
                          <option value="family">Family Size</option>
                          <option value="age">Years Remaining</option>
                        </select>
                      </div>

                      {/* High-Value Patents Table */}
                      <div className="bg-white rounded shadow p-4">
                        <h4 className="font-medium mb-3">
                          High-Value Patents (Score ≥ {valuationMinScore})
                        </h4>
                        <div className="patent-results-table-wrapper">
                          <table className="min-w-full">
                            <thead className="patent-results-table-wrapper thead">
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-semibold w-36">Patent No.</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Title</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Applicant</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Technology</th>
                                <th className="px-4 py-2 text-center text-sm font-semibold">Percentile</th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">Score</th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">Citations</th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">Family Size</th>
                                <th className="px-4 py-2 text-right text-sm font-semibold">Years Left</th>
                              </tr>
                            </thead>
                            <tbody>
                              {valuationData && valuationData.valuations
                                .filter(v => v.valuation.score >= valuationMinScore)
                                .sort((a, b) => {
                                  if (valuationSortBy === 'score') return b.valuation.score - a.valuation.score;
                                  if (valuationSortBy === 'citations') return b.valuation.components.citations - a.valuation.components.citations;
                                  if (valuationSortBy === 'family') return b.valuation.components.familySize - a.valuation.components.familySize;
                                  if (valuationSortBy === 'age') return b.valuation.components.yearsRemaining - a.valuation.components.yearsRemaining;
                                  return 0;
                                })
                                .slice(0, 100)
                                .map(({ patent, valuation }, index) => {
                                  const { p25, p50, p75 } = valuationData.portfolioMetrics.percentiles;
                                  const percentileLabel = valuation.score >= p75 ? 'Top 25%' :
                                                         valuation.score >= p50 ? '50-75%' :
                                                         valuation.score >= p25 ? '25-50%' : 'Bottom 25%';
                                  return (
                                  <tr key={`${patent.patentNo}-${index}`} className="border-b hover:bg-gray-50">
                                    <td className="px-4 py-2 text-sm font-medium">{patent.patentNo}</td>
                                    <td className="px-4 py-2 text-sm max-w-xs truncate" title={patent.title}>
                                      {patent.title || 'N/A'}
                                    </td>
                                    <td className="px-4 py-2 text-sm">{patent.applicant}</td>
                                    <td className="px-4 py-2 text-sm">{patent.l4 || 'N/A'}</td>
                                    <td className="px-4 py-2 text-xs text-center">
                                      <span className={`px-2 py-1 rounded ${
                                        valuation.score >= p75 ? 'bg-green-100 text-green-700' :
                                        valuation.score >= p50 ? 'bg-blue-100 text-blue-700' :
                                        valuation.score >= p25 ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                                      }`}>
                                        {percentileLabel}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right">
                                      <span className={`font-bold ${valuation.score >= p75 ? 'text-green-600' :
                                          valuation.score >= p50 ? 'text-blue-600' :
                                            valuation.score >= p25 ? 'text-yellow-600' : 'text-gray-600'
                                        }`}>
                                        {valuation.score}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right">{valuation.components.citations}</td>
                                    <td className="px-4 py-2 text-sm text-right">{valuation.components.familySize}</td>
                                    <td className="px-4 py-2 text-sm text-right">{valuation.components.yearsRemaining}</td>
                                  </tr>
                                )})
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* MY COLLECTION TAB */}
              {activeTab === 'advanced' && (
                <div>
                  <h3 className="text-lg font-semibold mb-4">My Collection</h3>
                  
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                    <p className="text-sm text-yellow-800">
                      ⚠️ <strong>Note:</strong> Starred patents are stored locally in your browser. They will be cleared if you clear your browser cache or history.
                    </p>
                  </div>
                  
                  {(() => {
                    const taggedList = Object.entries(taggedPatents).map(([patentNo, data]) => {
                      const patent = patentData.find(p => p.patentNo === patentNo);
                      return { patentNo, ...data, patent };
                    }).filter(t => t.patent);
                    
                    if (taggedList.length === 0) {
                      return (
                        <div className="bg-gray-50 rounded p-8 text-center">
                          <p className="text-gray-600 mb-2">No tagged patents yet</p>
                          <p className="text-sm text-gray-500">Click ⭐ on patents in other views to add them to your collection</p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="bg-white rounded shadow p-4">
                        <div className="flex justify-between items-center mb-4">
                          <div className="text-sm text-gray-600">
                            {taggedList.length} flagged patent{taggedList.length !== 1 ? 's' : ''}
                          </div>
                          <button
                            onClick={() => {
                              const csv = [
                                ['Patent No', 'Title', 'Applicant', 'Technology', 'Notes', 'Tagged Date'],
                                ...taggedList.map(({ patentNo, patent, note, timestamp }) => [
                                  patentNo,
                                  patent.title || '',
                                  patent.applicant || '',
                                  patent.l4 || '',
                                  note || '',
                                  new Date(timestamp).toLocaleDateString()
                                ])
                              ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
                              
                              const blob = new Blob([csv], { type: 'text/csv' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `tagged-patents-${new Date().toISOString().split('T')[0]}.csv`;
                              a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 border rounded hover:bg-gray-50"
                            title="Export flagged patents to CSV"
                          >
                            <i data-lucide="download" style={{ width: '16px', height: '16px' }}></i>
                            Export CSV
                          </button>
                        </div>
                        
                        <div className="patent-results-table-wrapper">
                          <table className="min-w-full">
                            <thead className="patent-results-table-wrapper thead">
                              <tr>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Flag</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Patent No.</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Title</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Applicant</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Technology</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Notes</th>
                                <th className="px-4 py-2 text-left text-sm font-semibold">Flagged</th>
                              </tr>
                            </thead>
                            <tbody>
                              {taggedList.map(({ patentNo, patent, timestamp, note }) => (
                                <tr key={patentNo} className="border-b hover:bg-gray-50">
                                  <td className="px-4 py-2">
                                    <button
                                      onClick={() => toggleTag(patentNo)}
                                      className="text-2xl hover:scale-110 transition-transform"
                                      title="Remove flag"
                                    >
                                      ⭐
                                    </button>
                                  </td>
                                  <td className="px-4 py-2 text-sm font-medium">{patentNo}</td>
                                  <td className="px-4 py-2 text-sm max-w-xs truncate" title={patent.title}>
                                    {patent.title || 'N/A'}
                                  </td>
                                  <td className="px-4 py-2 text-sm">{patent.applicant}</td>
                                  <td className="px-4 py-2 text-sm">{patent.l4 || 'N/A'}</td>
                                  <td className="px-4 py-2">
                                    <input
                                      type="text"
                                      value={note || ''}
                                      onChange={(e) => {
                                        setTaggedPatents(prev => ({
                                          ...prev,
                                          [patentNo]: { ...prev[patentNo], note: e.target.value }
                                        }));
                                      }}
                                      placeholder="Add notes..."
                                      className="w-full p-1 text-sm border rounded"
                                    />
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-500">
                                    {new Date(timestamp).toLocaleDateString()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Guide Modal */}
      <HelpGuide show={showGuideModal} onClose={() => setShowGuideModal(false)} />

      {/* Footer */}
      <div className="customer-footer">
        <div className="customer-footer-content">
          <div className="customer-footer-inner">
            <div style={{ color: '#94a3b8' }} className="text-sm">&copy; 2025{new Date().getFullYear() > 2025 ? `-${new Date().getFullYear()}` : ''} PatentsPrism. All rights reserved.</div>
            <div className="footer-badges">
              <div style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i data-lucide="shield-check" style={{ width: '14px', height: '14px', color: '#93c5fd' }}></i> SOC 2
              </div>
              <div style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i data-lucide="shield-check" style={{ width: '14px', height: '14px', color: '#93c5fd' }}></i> ISO 27001
              </div>
              <div style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i data-lucide="shield-check" style={{ width: '14px', height: '14px', color: '#93c5fd' }}></i> GDPR
              </div>
              <div style={{ background: 'rgba(255, 255, 255, 0.1)', color: '#cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i data-lucide="shield-ban" style={{ width: '14px', height: '14px', color: '#93c5fd' }}></i> No Training
              </div>
            </div>
            {selectedStudy?.landscapeDate && (
              <span style={{ color: '#94a3b8', fontSize: '14px' }}>
                Landscaped: {new Date(selectedStudy.landscapeDate + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN APP
// ============================================

export { CustomerView };
